// ============================================================================
// logo-outline.js — logo outline PEEL machinery (A: auto-conflict · B: manual)
// ============================================================================
// The plate's TEXT peels an outermost outline that merges with a dyed plate base
// (design-profile.effectiveTextStyle — a free, reversible VECTOR view). The LOGO
// can't do that for free: its outlines are baked into the RASTER. So we peel a
// band off the raster instead — a border-connected flood that knocks out the
// outermost detected band colour, outside-in, exactly like a background key.
//
// Two callers share this:
//   • A (auto): when the plate is dyed a colour that merges with the logo's own
//     OUTERMOST band (red logo ring on a red plate looks goofy), peel that band —
//     `conflictPeelCount` mirrors effectiveTextStyle's ΔE rule, outside-in.
//   • B (manual): the buyer turns a detected outer band off in the Colors panel.
// The EFFECTIVE peel = max(auto, manual); both only ever remove from the edge in.
//
// The ORIGINAL logo raster is never mutated — peel 0 returns it untouched, so the
// whole thing is reversible (un-dye / toggle back = the band returns). Only a tiny
// count is stored on the profile; the peeled raster is regenerated on demand.
// ============================================================================

import { OUTLINES_MAX } from './design-profile.js';
import { colorsConflict, colorsIndistinguishable, colorsSameShade, contrastRatio, relLuminance, nearMerges, normalizeHex, fillContrastRemap, isNearBlackHex, isNearNeutralHex, readsOn, SAME_COLOR_DELTA_E, TONE_MERGE_DELTA_E, LOGO_BOX_NOMINAL_VB, outlineThicknessFor, ringWeightFromFrac, logoGapFromStructure, contrastPoolFrom } from './design-profile.js';
import { INK_PIECE_SOLID_SHARE, pieceMaskForTone } from './bg-detect.js';
import { srgbToLab, deltaE } from './palette-extract.js';

/* ══ WHICH ROW OF THE CENSUS IS THIS HEX? — THE CROSS-TABLE JOIN, AND ONLY THAT (WD 427) ═════════
 * ⭐ Ryan's pass-seven audit filed causes 2, 3, 4 and 5 as ONE root — *"the app has three different
 * ideas of when two hexes are the same colour… decide what 'the same ink' means once and make every
 * consumer ask the same question."* Measured across all 528 plates, that is **half right**, and the
 * half it gets wrong is the half that would have cost approved plates.
 *
 * There are TWO questions here, not one, and they have different answers:
 *
 *   1 · THE CROSS-TABLE JOIN — a hex the app is HOLDING (a band, a palette role, a swap's `from`)
 *       against the RASTER census. Two quantisers, so one ink gets two spellings by construction
 *       (WD 424's finding). This was EXACT STRING EQUALITY and that is plainly wrong:
 *           athletics-gcc-edu   band #ED1F25 vs tone #EB1F25   ΔE 0.83   ->  the repaint was
 *           applied to the artwork and never carried to the tone, so on its own red plate the
 *           mark turned black and the number kept the old red. Cause 2, exactly.
 *       `sameToneRow` is that join now. Measured: 3 flagged plates move (gcc cfg1 + cfg4 out of
 *       the plate's own red, gbp cfg2 onto the blue its mark is actually drawn in) and **ZERO
 *       approved plates move, and zero artwork repaints change.**
 *
 *   2 · WITHIN-CENSUS AGGREGATION — two ROWS of the same table that are one ink split by
 *       anti-aliasing (`bgFaceShare`, `touchedTones`, `toneInkShare`, `wrapToneFromTouches`).
 *       ⛔ THIS ONE STAYS AT ΔE 14, AND NOT BECAUSE NOBODY HAS MEASURED IT. Tightening it to ΔE 2
 *       clears Ryan's cause-4 flag and breaks three plates he approved, because the corpus says
 *       there is no bar at all:
 *           MUST POOL      oakton-owls   #01817A <-> #007983   ΔE 11.43   approved
 *           MUST NOT POOL  120150001516  #F8F8F8 <-> #D1D2D4   ΔE 13.45   HIS FLAG (cause 4)
 *           MUST POOL      broncos-1     #1E2D55 <-> #292439   ΔE 14.00   approved
 *       The two that must pool STRADDLE the one that must not. Colour distance cannot separate
 *       "one ink the encoder split" from "two inks the designer chose" — causes 4 and 5 are not a
 *       threshold, and the next attempt needs a different axis (⚠ not spatial adjacency: WD 425
 *       already measured and disproved that one on bobcats' gold).
 *
 * ⚠ AND CAUSE 3 IS NOT IN THIS FAMILY AT ALL — see `contextualFillSwap`'s `facesPlate`, where the
 * fail-closed repair is written down as measured and rejected.
 *
 * `colorsConflict` is not a candidate for either and says so in its own docstring — it answers
 * *"would these two read as one EDGE?"*, a looser question, and it is the right predicate for a
 * band against a plate, where it stays. The identity predicate already existed:
 * `colorsIndistinguishable` at ΔE 2, written for exactly this ("#FDFDFD and #FEFEFE differ by one
 * step per channel").
 *
 * ⚠ IT IS NOT THE TWIN-MERGE BAR AND MUST NOT BE COLLAPSED INTO ONE. `sameInk` (TWIN_DELTA_E, 14)
 * asks whether two rows the census SPLIT are one ink whose vote should be added back together —
 * measured at (18.4, 19.2) in WD 425, nine times looser than this. Three questions, three answers.
 *
 * `opts.toneRowDeltaE` overrides the bar for measurement, like every other knob in this file. */
const TONE_ROW_DELTA_E = SAME_COLOR_DELTA_E;
/** True when `a` and `b` are the same row of the tone census — NOT "would they read as one edge". */
export function sameToneRow(a, b, opts = {}) {
  const x = normalizeHex(a);
  const y = normalizeHex(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const bar = typeof opts.toneRowDeltaE === 'number' ? opts.toneRowDeltaE : TONE_ROW_DELTA_E;
  return bar === SAME_COLOR_DELTA_E ? colorsIndistinguishable(x, y) : deltaE(labOf(x), labOf(y)) < bar;
}

const hexToRgb = (hex) => {
  const h = normalizeHex(hex) || '#000000';
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const isNearBlack = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return r <= 24 && g <= 24 && b <= 24; // undyed rubber → no conflict, no peel
};

/**
 * The logo's OUTLINE band colours, OUTER → INNER (the order the raster peels), from
 * a LogoStructure (whose `outlines` run inner → outer). The fill is never a band.
 * @param {{confidence?:string, outlines?:{hex:string}[]}|null} structure
 * @returns {string[]} normalized hexes, outermost first ([] when not high-confidence)
 */
export function outerBandsFromStructure(structure) {
  if (!structure || structure.confidence !== 'high' || !Array.isArray(structure.outlines)) return [];
  return structure.outlines
    .map((o) => normalizeHex(o && o.hex))
    .filter(Boolean)
    .reverse(); // inner→outer  ⇒  outer→inner
}

/**
 * How many OUTER bands merge with the dyed plate base (ΔE), outside-in, stopping at
 * the first distinct band — mirrors effectiveTextStyle so the logo peels in lockstep
 * with the text. Black / undyed base → 0.
 * @param {string[]} bandsOuterToInner
 * @param {string} bodyColor
 */
/* ⛔ THE NEAR-BLACK BAIL IS GONE (Ryan 2026-08-10, the Cherry Valley crest: *"the black portion of
 * the logo on the black plate should turn white. Simple as that."*).
 *
 * 🔑 THIS PROJECT HAS ALREADY FIXED THIS EXACT GUARD ONCE, on the text side, on Ryan's report — see
 * `assignOutlineColors` in ui/state.js, 2026-08-05: *"A BLACK PLATE BITES TOO… Black is not a plate
 * that cannot bite — it is the plate that bites HARDEST, because it is the one every default
 * near-black ink disappears into."* The identical predicate lived here, guarding the LOGO's bands,
 * and was never given the same correction. One wrong premise, two files.
 *
 * Cherry Valley is a rose fill inside a near-black keyline. On the sequence's black plate that
 * keyline rendered black-on-black — present in the model, invisible on the plate, and unreachable by
 * any rule because this returned 0 before anything could look at it.
 *
 * ⚠ ONE CALLER (main.js's band-repaint block), and what it does with a non-zero count is REPAINT,
 * not remove — the 2026-07-28 rule. So lifting this cannot delete a band; it can only give an
 * invisible one a colour. */
export function conflictPeelCount(bandsOuterToInner, bodyColor) {
  const base = normalizeHex(bodyColor);
  if (!base || !Array.isArray(bandsOuterToInner)) return 0;
  let count = 0;
  for (const band of bandsOuterToInner) {
    /* ⚠ `nearMerges` (the ΔE 14–26 ghost zone) is deliberately NOT part of this trigger. It was,
     * for one commit (BC Tornados' slate spiral, ΔE 15.3 from the dealt slate plate, "super
     * blurry") — and the flood repaint SHREDDED the hairline linework it was trying to rescue:
     * 1px AA strokes never label cleanly, the same physics as the pass-5 keyline peel. The
     * remedy that shipped instead is upstream: `applyDefaultPlateColors` refuses to DEAL a plate
     * the whole mark ghosts on, so the broken pairing never renders by default. */
    if (band && colorsConflict(base, band)) count += 1;
    else break;
  }
  return count;
}

/**
 * What the AUTO conflict pass does to a logo's outline bands on a given plate.
 *
 * ⛔ EXTRACTED SO THERE IS ONE DEFINITION OF THIS RULE, NOT TWO (Ryan 2026-08-12). The raster
 * pipeline (`main.js` `syncLogoRaster`) has always owned it, and then the Customize-Design swatch
 * needed the same answer — it draws the mark's tones as bands and was drawing the STORED artwork,
 * so a rescued Cherry Valley crest rendered rose+white on the plate while its own chip claimed
 * rose+black. Re-deriving the rule beside the chip is exactly the "two places need the same answer,
 * one of them will be wrong" trap in the project CLAUDE.md §0b.
 *
 * `repaint[i] = hex` when there is a legible colour for a merging band; `peel` lists the index when
 * there is not — a ring the buyer cannot see is worse than a ring that is gone.
 *
 * @param {string[]} effBands  bands OUTER→inner, as they currently render (manual override or detected)
 * @param {string} plateHex    the plate this is being judged against — the EFFECTIVE one, never a
 *                             stored dye on a product that cannot wear it
 * @param {{roles?: string[], avoid?: string|null}} [opts]
 *   `roles` — the mark's palette roles, so the rescue can reach for a brand colour first.
 *   `avoid` — the fill the band WRAPS. Without it the only non-conflicting brand colour can be that
 *   fill, and the keyline is repainted to it: the outline then disappears by being absorbed instead
 *   of by being invisible. Excluded, the remap falls to the neutral and the keyline turns white.
 * @returns {{repaint: Record<number,string>, peel: number[]}}
 */
/**
 * The colours a conflicting band may be rescued WITH, in the order the colour model states:
 * the palette roles, then the buyer's bench.
 *
 * ⛔ THE BENCH IS THE POINT, AND ITS ABSENCE IS A BUG THIS PROJECT HAS ALREADY PAID FOR ONCE
 * (Ryan 2026-08-12, `RING-3tone-life-ring.png` on Biggest Badge over a white plate: *"how come
 * there are random black splotches added around the logo perimeter?"*).
 *
 * That mark is white-bodied with an orange fill and a tan rope. On a white plate its outermost band
 * — the white body — genuinely vanishes, so the rescue is right to fire. But only primary and
 * secondary become palette ROLES; `applyPalette` seats a logo's THIRD tone on the bench via
 * `addSwatch(..., { ownTone: true })`. Both callers passed roles only, so the pool was
 * {orange, white}: white is the band itself and orange is `avoid`ed as the fill it wraps — leaving
 * nothing, and `fillContrastRemap` fell to its last resort, #000000. Black wedges wherever the rope
 * waved inward and the repainted band showed.
 *   With the bench in the pool the same call answers `#D5A963`, the mark's own tan. Measured.
 *
 * ⚠ THIS IS THE SAME SENTENCE `applyAutoStyleToElement` ALREADY CARRIES — *"every plate-aware
 * chooser we had reads roles only, so their grey was invisible to the code"* (Ryan 2026-08-07, the
 * Bauers grey). It was fixed there and not here, because the two ladders were written months apart.
 * Hence one exported pool rather than the identical array literal in two files.
 */
export function bandRescuePool(profile) {
  const p = profile || {};
  const pal = p.palette || {};
  return [pal.primary, pal.secondary, pal.tertiary, ...(Array.isArray(p.swatches) ? p.swatches : [])];
}

// ── THE CHEAP TEST THAT SERVES EVERY REPAINT (Ryan 2026-08-18) ──────────────────────────────────
// "After the proposed change, would any two regions that TOUCH end up the same colour? If yes,
// don't repaint." Edison's blue linework was repainted white to clear a blue plate and fused with
// the white teeth and lettering it draws; Mission Patriots' white was repainted navy on a white
// plate and the patriot's face vanished into the hat it sits on. Every plate-side contrast test
// passed both repaints, because the collision is with the mark's OWN tones, which nothing at the
// choosing site could see. `logoStructure.toneTouches` (measured at extraction, from the same
// boundary tally the band analysis has always used) is what makes the question answerable by a
// chooser holding only hexes.
/** Shared boundary, as a share of the smaller tone's total boundary, at/above which two tones
 *  "touch". ⚠ Provisional until probe-tone-touches runs the corpus — re-measure before trusting
 *  it near the bar. Far under any real keyline contact (a wrapped fill runs ~half its boundary
 *  against the wrapper) and far over anti-aliasing accidents. */
export const TONE_TOUCH_MIN_SHARE = 0.03;
/**
 * Do tones `hexA` and `hexB` share an edge in the artwork this table was measured from?
 * Table keys are matched by ΔE (`colorsConflict`), not string equality — a rescue target of
 * house #FFFFFF must find the mark's own #FDFDFD — and near-duplicate keys are AGGREGATED on
 * both sides, since one visual tone often splits across two extracted swatches.
 * `touches` null/absent (an older save, an early-exit analysis) → false: unknown reads as
 * "no veto", which is exactly the pre-2026-08-18 behaviour.
 */
/**
 * The tones `hexA` shares an edge with in the artwork, as clusters of near-duplicate table keys
 * (one visual tone often splits across two extracted swatches), each with its contact share of
 * A's own boundary. 'BG' and A itself are excluded. [] when the table is absent (older save).
 */
export function touchedTones(touches, hexA) {
  if (!touches || typeof touches !== 'object') return [];
  const a = normalizeHex(hexA);
  if (!a) return [];
  const isA = (hex) => hex === a || colorsConflict(hex, a);
  const aKeys = Object.keys(touches).filter((k) => { const kh = normalizeHex(k); return kh && isA(kh); });
  if (!aKeys.length) return [];
  let boundary = 0;
  const clusters = []; // { rep, contact }
  for (const k of aKeys) {
    const row = touches[k];
    if (!row || !row.boundary) continue;
    boundary += row.boundary;
    for (const t of Object.keys(row.touches || {})) {
      if (t === 'BG') continue;
      const th = normalizeHex(t);
      if (!th || isA(th)) continue; // anti-aliased near-duplicates of A are A, not a neighbour
      const cl = clusters.find((c) => colorsConflict(c.rep, th));
      if (cl) cl.contact += row.touches[t]; else clusters.push({ rep: th, contact: row.touches[t] });
    }
  }
  if (!boundary) return [];
  // Each cluster's INK MASS as a share of the whole table's ink — the damage term a veto weighs.
  let totalPx = 0;
  for (const k of Object.keys(touches)) totalPx += (touches[k] && touches[k].px) || 0;
  const pxOf = (rep) => {
    let px = 0;
    for (const k of Object.keys(touches)) {
      const kh = normalizeHex(k);
      if (kh && (kh === rep || colorsConflict(kh, rep))) px += (touches[k] && touches[k].px) || 0;
    }
    return px;
  };
  return clusters
    .map((c) => ({ hex: c.rep, share: c.contact / boundary, inkShare: totalPx ? pxOf(c.rep) / totalPx : 0 }))
    .filter((c) => c.share >= TONE_TOUCH_MIN_SHARE);
}

/** Fusion test — does tone `hexB` exist adjacent to tone `hexA` in this artwork? */
export function tonesTouch(touches, hexA, hexB) {
  const b = normalizeHex(hexB);
  if (!b) return false;
  return touchedTones(touches, hexA).some((c) => c.hex === b || colorsConflict(c.hex, b));
}

/**
 * What share of tone `hexA`'s boundary faces the BACKGROUND — i.e. where the PLATE will show
 * against it (Ryan 2026-08-23, the ECC Kats: *"the sections of the logo that were red and are now
 * white don't even touch any part of the red plate anyway"* — measured: the claws' red faces BG
 * 0.0%, the letters' white 0.4%, while the outlines that really meet the plate face it 10%+).
 * A tone that never meets the plate cannot merge with it and needs no contextual rescue.
 * Near-duplicate keys aggregate exactly as `touchedTones` does. Null table → null (unknown).
 */
/**
 * What SHARE of the mark's ink this tone is — the same question `toneDiag[].pxShare` answers in the
 * corpus dumps, asked at runtime. Ladder: the measured touch table first, the extractor palette's
 * populations second (a mark whose structure read FAILED has no table — layton-high-school is
 * exactly that case and is the reason this fallback exists), and `null` when neither can answer.
 *
 * ⚠ `population` at runtime, `pop` in a dump — same field, the corpus runner renames it.
 */
export function toneInkShare(profile, hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  const ls = (profile && profile.logoStructure) || null;
  const touches = ls && ls.toneTouches;
  if (touches && typeof touches === 'object') {
    let mine = 0;
    let total = 0;
    for (const k of Object.keys(touches)) {
      const kh = normalizeHex(k);
      const px = (touches[k] && touches[k].px) || 0;
      if (!kh || !px) continue;
      total += px;
      if (kh === h || colorsConflict(kh, h)) mine += px;
    }
    if (total > 0) return mine / total;
  }
  /* ⛔ AND THE FALLBACK IS `logoTraceTones`, NOT `palette.all` — THE BLIND-AXIS TRAP, AGAIN.
   * The first cut of this read `profile.palette.all`, which exists in the corpus DUMP (that field is
   * `state.ui.palette`, the extractor's) and does NOT exist on the design profile, whose `palette`
   * is the ROLES object — primary / secondary / tertiary and nothing else. So the offline score said
   * layton-high-school cfg3 at 80% and the running app read `undefined`, scored 0, and the gate
   * never fired: a 17-mark probe moved not one plate, including the plate the rule was written for.
   * `p.logoTraceTones` (WD 414) is the runtime answer and is already what `syncLogoContrastRing`
   * falls back to on exactly these structure-less marks. Layton reads 0.8005 there. */
  const trace = (ls && Array.isArray(ls.traceTones) && ls.traceTones)
    || (profile && Array.isArray(profile.logoTraceTones) && profile.logoTraceTones)
    || [];
  let mine = 0;
  let total = 0;
  for (const t of trace) {
    const kh = normalizeHex(t && t.hex);
    const share = (t && t.share) || 0;
    if (!kh || !share) continue;
    total += share;
    if (kh === h || colorsConflict(kh, h)) mine += share;
  }
  return total > 0 ? mine / total : null;
}

/** The share of the mark's WHOLE background frontier this tone (and its conflict-near rows) holds —
 *  the monopoly measure `wrapToneFromTouches` computes inline, as a function. 0 with no table. */
export function frontierShare(touches, hexA) {
  if (!touches || typeof touches !== 'object') return 0;
  const a = normalizeHex(hexA);
  if (!a) return 0;
  let total = 0, own = 0;
  for (const k of Object.keys(touches)) {
    const bg = (touches[k] && touches[k].touches && touches[k].touches.BG) || 0;
    total += bg;
    const kh = normalizeHex(k);
    if (kh && (kh === a || colorsConflict(kh, a))) own += bg;
  }
  return total > 0 ? own / total : 0;
}

export function bgFaceShare(touches, hexA) {
  if (!touches || typeof touches !== 'object') return null;
  const a = normalizeHex(hexA);
  if (!a) return null;
  const isA = (hex) => hex === a || colorsConflict(hex, a);
  let boundary = 0;
  let bg = 0;
  for (const k of Object.keys(touches)) {
    const kh = normalizeHex(k);
    if (!kh || !isA(kh)) continue;
    const row = touches[k];
    if (!row || !row.boundary) continue;
    boundary += row.boundary;
    bg += (row.touches && row.touches.BG) || 0;
  }
  return boundary ? bg / boundary : null;
}

/** Under this BG-facing share a tone is WRAPPED — the plate never shows beside it. Measured on
 *  ECC: wrapped tones at 0.000/0.004, real plate-facers at 0.106/0.110 — open space either side. */
export const WRAPPED_TONE_BG_MAX = 0.03;

/** ⛔ A tone that fails `readsOn` against a NEAR-BLACK plate is only rescued once it is at least
 *  this much of the mark's ink — below it, a dark accent being swallowed is a detail, not the mark
 *  disappearing. See `contextualFillSwap`'s `lostInk` gate for why the plate gate alone is not enough.
 *
 *  ⭐ IT SITS IN AN EMPTY BAND RATHER THAN ON A PLATE, which is the only reason a number is
 *  allowed here at all. Measured over the 2026-09-17 render, every near-black candidate in the
 *  corpus, sorted by share:
 *        85.9%  db-logo cfg2 + cfg4     FLAGGED
 *        80.1%  layton cfg3             FLAGGED   <- the plate Ryan asked about
 *      ───── nothing lives between 61.7% and 80.1% ─────  0.70 is the middle of it
 *        61.7%  leeuflames cfg2         approved
 *        57.7%  philadelphia-pd cfg4    approved
 *        44.7%  KENYON cfg4             approved  <- the gold control, 25 points clear
 *  0.65 and 0.70 score identically — the band is empty, so the exact figure inside it changes
 *  nothing. At 0.40 it moves Kenyon and the rule is dead.
 *
 *  ⭐ PROBED THROUGH THE REAL APP (17 marks, then 6): it moves exactly ONE plate, layton cfg3,
 *  and breaks nothing. db-logo cfg2 + cfg4 clear this bar at 86% and are still refused — by the
 *  KENYON TEST (`unmeasuredPresence`, WD 337): their white is in the trace, they carry no touch
 *  table, so no swap target can be measured and the walk ends empty. That is the existing guard
 *  doing its job, not a gap. An offline score that modelled the gate but NOT the `validate` veto
 *  claimed three; the probe is what corrected it.
 *
 *  ⚠ THE BAR IS A SHARE AND NOT A CONTRAST RATIO, because contrast does not separate these at all:
 *  layton cfg3 is the worst-reading plate in the group at 1.18:1 and two plates Ryan APPROVED sit at
 *  1.24:1 right behind it.
 *
 *  ⚠ FOUR MARKS CANNOT BE MEASURED AND ARE THEREFORE NEVER RESCUED — bctornados, bluffton,
 *  gobuffsgo and hiuroyals carry neither a touch table nor trace tones, so the share reads 0. Two of
 *  those are open flags this rule does NOT fix; silence beats guessing on an unmeasurable mark. */
export const MARK_LOST_INK_MIN = 0.70;

/** A wrapped tone must spend at least this share of its boundary against the wrap tone. */
export const WRAP_CONTACT_MIN = 0.6;
/**
 * The SECOND wrap construction: FRONTIER MONOPOLY (Ryan 2026-08-23, Angry Panda — *"this logo,
 * which clearly has an orange outline"*, yet the text seeded bare). The panda is not a badge with
 * one tone buried in the wrap: it is a seven-tone assembly whose tones mostly touch EACH OTHER,
 * and its orange keyline wraps the whole assembly — no single tone rides it for
 * `WRAP_CONTACT_MIN` of its own boundary (the dark outline manages 0.24, the rest ~0). The fact
 * the eye uses there is different: the orange is the ONLY tone that faces the background at all —
 * it owns the mark's entire background frontier (measured 1.00; every other row's BG count is 0).
 * So a bg-facing tone also qualifies when it owns ≥ this share of the mark's TOTAL background
 * boundary, with buried tones only needing to touch it at all (≥ `TONE_TOUCH_MIN_SHARE`, via
 * `touchedTones`) rather than ride it. Controls: ECC's frontier is SPLIT dark 0.53 / gray 0.47 —
 * both far under the bar, and ECC keeps qualifying through the ride construction untouched.
 */
export const WRAP_FRONTIER_OWN_MIN = 0.9;
/**
 * The mark's DE-FACTO KEYLINE when the band detector declined it (Ryan 2026-08-23, ECC Kats:
 * *"why on the gray plate with white text is it not assigning a thin black outline to the text
 * as the default? that'd match…"* — the structure came back low-confidence, so `markWearsKeyline`
 * said no, while the eye plainly sees the dark outline wrapping every element). The touch table
 * carries the same fact the eye uses: a WRAP tone faces the background itself
 * (`bgFaceShare ≥ WRAPPED_TONE_BG_MAX`) while at least one other tone is buried against it
 * (bgFace < the bar AND ≥ WRAP_CONTACT_MIN of its boundary on the wrap tone). ECC: the dark
 * faces BG at 0.106 and the red claws / white letters ride it for effectively their whole
 * boundary. Amherst's demoted seal does NOT qualify (its white shell faces BG at 0.11 — nothing
 * is buried), so this reaches exactly the outlined-badge class, not every low-confidence mark.
 * Returns the wrap tone's hex, or null.
 */
export function wrapToneFromTouches(touches) {
  if (!touches || typeof touches !== 'object') return null;
  const keys = Object.keys(touches).map((k) => normalizeHex(k)).filter(Boolean);
  let totalBG = 0;
  for (const k of Object.keys(touches)) totalBG += (touches[k] && touches[k].touches && touches[k].touches.BG) || 0;
  let best = null;
  for (const w of keys) {
    const wBg = bgFaceShare(touches, w);
    if (wBg === null || wBg < WRAPPED_TONE_BG_MAX) continue;
    // Frontier ownership — what share of the MARK's whole background boundary this tone holds
    // (aggregated over conflict-near rows, as bgFaceShare aggregates).
    let wOwnBG = 0;
    for (const k of Object.keys(touches)) {
      const kh = normalizeHex(k);
      if (kh && (kh === w || colorsConflict(kh, w))) wOwnBG += (touches[k] && touches[k].touches && touches[k].touches.BG) || 0;
    }
    const monopolist = totalBG > 0 && wOwnBG / totalBG >= WRAP_FRONTIER_OWN_MIN;
    let wrappedInk = 0;
    for (const t of keys) {
      if (t === w || colorsConflict(t, w)) continue;
      const tBg = bgFaceShare(touches, t);
      if (tBg === null || tBg >= WRAPPED_TONE_BG_MAX) continue;
      const onW = touchedTones(touches, t).find((c) => c.hex === w || colorsConflict(c.hex, w));
      if (onW && (onW.share >= WRAP_CONTACT_MIN || monopolist)) wrappedInk += onW.inkShare || 0;
    }
    if (wrappedInk > 0 && (!best || wrappedInk > best.ink)) best = { hex: w, ink: wrappedInk };
  }
  return best ? best.hex : null;
}

/** How much of the mark's ink a repaint may FUSE before the repaint is worse than showing the
 *  plate through. The poles, measured: RIGAR's white letter counters (~2–5% of the ink — fuse
 *  them and the inverted wordmark is a net win, Ryan's own design for that mark) · Kenyon's gray
 *  chest at **0.089** (Ryan 2026-08-23: the white→gray repaint on the white plate fused the
 *  wildcat's gray chest into its new gray keyline — *"gray on gray… flattens and ruins it"* —
 *  and 0.089 sat just under the old provisional 0.10, the exact near-the-bar case the previous
 *  comment warned about) · Edison / Mission at double digits. 0.07 sits in the open space
 *  between RIGAR and Kenyon. With Kenyon vetoed, the merging band peels, `bandsVisibleOnPlate`
 *  goes false, and the contextual ring machinery hands mark AND text a gray OUTER ring — the
 *  add-don't-repaint remedy Ryan asked for on this exact mark. */
export const FUSE_MAX_INK_SHARE = 0.07;

/* == KEEP THE MARK AND OUTLINE IT - THE DEFAULT, AND RYAN'S TWO EXCEPTIONS (WD 440b, 2026-09-19) ==
 *
 * ⭐ THE DEFAULT INVERTED. For most of this tool's life a mark's tone that collided with its plate
 * was RECOLOURED, and the alternative - leave the artwork alone and add a ring around it - was
 * something Ryan asked for by hand, three times, on three different marks, in almost the same
 * sentence. A nine-plate question page settled it: six "Mark kept" against three "Recoloured",
 * every one of the three carrying a written exception. So the recolour is now the exception and
 * keeping the mark is the rule.
 *
 * ⛔ FOUR AXES DIED BEFORE THIS ONE, and none of them was about colour distance. Depth x luminance
 * flip (WD 412), connected-REGION count (WD 414), the ink SHARE he picked himself and the
 * tone-collision axis (both WD 440) each described his approvals at least as well as his flags.
 * ⭐ What finally separated them was asking about the REMEDY rather than the fault - showing him
 * the two things the app could DO, side by side, and then implementing his sentences literally.
 * These two exceptions are those sentences; they are not a model fitted to nine points.
 *
 *   1. *"in a two tone logo like this where we have one color and one neutral (black or white),
 *      then I'm okay with black becoming white or white becoming black."*
 *   2. *"one free-floating part of the logo which is one color, and another free-floating part
 *      which is another color... we can turn the entire logo to the non-clashing tone knowing it's
 *      not going to break the logo."*
 *
 * ⚠ EXCEPTION 1 IS A TONE-KIND TEST, NOT A TONE COUNT - and that is measured, not stylistic. Both
 * Cherry Valley marks are two-tone to the eye and to Ryan (*"it should change the 2-tone logo from
 * black/pink to white/pink"*), and the census reports THREE tones for each: a 3.8% and a 3.6%
 * near-neutral blend sitting beside the black. A count test keeps the very plates he asked to have
 * recoloured. So the question this asks is his: is there ONE colour here, with the rest neutral?
 * `isNearNeutralHex` is the app's own 2026-08-10 measurement of that (chroma < 18, with the corpus
 * empty between 12.5 and 24.4) - no new constant, and no bar invented to fit these plates.
 *
 * ⚠ HIS SUB-EXCEPTION NEEDS NO NEW CONSTANT EITHER - *"if we went from black to white and the
 * white clashed too much with the color (the color was a really pale pink for example) then we'd be
 * better off going with MARK KEPT"* is `readsOn(newNeutral, colour)`, the app's own 2.0:1 bar, and
 * it lands where he wants it: Cherry Valley's pink reads at 2.10 and 2.77 and PASSES on the existing
 * bar. ⛔ NOTHING WAS LOOSENED FOR IT AND NOTHING SHOULD BE. He first asked for the bar to be
 * *"loosened slightly so that shade of pink just gets by"*, then described the picture he actually
 * wanted - which is what the corpus already renders. A threshold change was requested, measured, and
 * turned out to be a description of a rendering fault one layer down. ⚠ That margin is 5%, so
 * extractor drift could flip it: worth watching, not tuning.
 *
 * ⛔ THIS GATE IS PURELY SUBTRACTIVE, which is what makes it safe to ship corpus-wide. It can only
 * ever REFUSE a recolour the app was already going to make; an exception restores exactly today's
 * behaviour, byte for byte. The one way it can make a plate WORSE is by taking a recolour away and
 * leaving nothing in its place - which is why both doors are wired so that a refusal ARMS the ring
 * (`syncLogoContrastRing` reads `contextualFillSwap` for "was this tone already rescued" and
 * `bandsVisibleOnPlate` for "did any band repaint"), and why the corpus pass ASSERTS it rather than
 * assuming it. A plate that loses its recolour and gains no ring is a regression.
 *
 * ⭐ A THIRD EXCEPTION ARRIVED FROM THE RENDERED PICTURES, NOT FROM THE SPEC. Six plates lost
 * their recolour and gained no ring; shown both versions, Ryan kept five and reversed one, and his
 * reason was a case neither of his sentences covered — a black tone on the black plate. See
 * `markRecolourAllowed`'s first clause.
 *
 * ⏳ NOT BUILT: his 3-tone extension (*"we'd change the blue part of the logo to the minority 3rd
 * color... if they match in terms of %, MARK KEPT"*). It is fully specified and NO mark in the
 * 132-mark corpus exercises it, so there is nothing to verify it against; `markRecolourAllowed`
 * reports such a mark instead of guessing at one.
 */

/** The mark facts this gate needs, gathered in ONE place so both doors ask the identical question.
 *  `bandViewOptions` carries it to the band door; `contextualFillSwap` builds its own. */
export function markToneRecord(profile) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const trace = (Array.isArray(ls.traceTones) && ls.traceTones)
    || (Array.isArray(p.logoTraceTones) && p.logoTraceTones) || [];
  return {
    tones: trace.filter((t) => t && t.hex && (t.share || 0) >= 0.03),
    pieces: (Array.isArray(ls.inkPieces) && ls.inkPieces)
      || (Array.isArray(p.logoInkPieces) && p.logoInkPieces) || [],
    /* WD 447 — per tone, the other tones it shares a shape with (`measureToneTouch`). What the
       NEUTRAL-TOUCH exception reads. Absent on every profile analysed before it existed, and the
       exception simply does not fire there rather than guessing. */
    neighbours: (Array.isArray(ls.toneNeighbours) && ls.toneNeighbours)
      || (Array.isArray(p.logoToneNeighbours) && p.logoToneNeighbours) || [],
  };
}

/** The distinct tones the mark's FREE-FLOATING solid pieces wear - exception 2's measurement. */
export function floatingToneSet(mark) {
  const pieces = (mark && Array.isArray(mark.pieces)) ? mark.pieces : [];
  return [...new Set(
    pieces.filter((x) => (x.dom || 0) >= INK_PIECE_SOLID_SHARE)
      .map((x) => normalizeHex(x.hex)).filter(Boolean),
  )];
}

/**
 * May this mark's tone be RECOLOURED on this plate, or does the mark get kept and ringed instead?
 *
 * @param {object} mark `markToneRecord(profile)`
 * @param {string} fromHex the tone the door wants to repaint
 * @param {string} toHex   what it wants to repaint it to
 * @returns {{allowed:boolean, why:string}} `why` names the exception, or the reason it missed - the
 *   corpus report reads it, so a refusal is never silent.
 */
export function markRecolourAllowed(mark, fromHex, toHex, plateHex, opts) {
  if (!mark || !Array.isArray(mark.tones) || mark.tones.length < 2) {
    return { allowed: false, why: 'keep' };
  }
  const from = normalizeHex(fromHex);
  const to = normalizeHex(toHex);
  const plate = normalizeHex(plateHex);
  /* ⭐ BLACK ON BLACK IS NOT A CHOICE, SO IT OUTRANKS BOTH TESTS BELOW (Ryan 2026-09-19, on
   * 120051002169 cfg4 after seeing it rendered): *"The recolored one takes a 2-tone logo with one
   * of the tones black, and flips the black to white so it can stand-out against the black plate
   * color. Essential in this case. The 'Mark Kept' makes the black portion of the logo be black on
   * black which doesn't work."*
   *   Both tests below ask whether the TARGET is a good colour. This asks something prior: whether
   * KEEPING is survivable at all. It is not when the tone being kept is the plate — the ink does not
   * go subtle, it is gone — and no ring around the outside brings an interior tone back.
   *
   * ⚠ IT REMOVES AN INCONSISTENCY RATHER THAN FITTING ONE PLATE. The corpus holds THREE
   * black-tone-on-the-black-plate recolours; cherry-valley cfg3 and cherry-valley-v2 cfg3 already
   * pass through exception 1, and 2169 was refused only because its census splits one cyan into two
   * rows 18.1 apart, so the *"one colour"* test saw two. Measured over all 528 plates, this newly
   * allows exactly ONE — 2169 cfg4, the plate he judged. Nothing else in the corpus reaches it.
   *
   * ⛔ AND THE FIX IS NOT TO WIDEN "ONE COLOUR" TO "ONE COLOUR FAMILY". That needs a sameness
   * threshold near 20 to merge #6BC6DC with #099EC5, justified by this single mark, against the
   * app's measured 14 — and it would still not be enough, because the dominant cyan reads at 1.96
   * against white and the sub-exception would refuse it anyway. Two invented changes to rescue one
   * plate. `isNearBlackHex` is the app's own predicate for the scope `contextualFillSwap` already
   * calls the one where *"the black plate bites HARDEST"*; no new constant. */
  if (from && to && plate && isNearBlackHex(plate) && isNearBlackHex(from) && isNearNeutralHex(to)) {
    return { allowed: true, why: 'black-on-black' };
  }
  // -- exception 1: one colour + neutrals, a NEUTRAL moves, and the colour still reads on it --
  const colours = mark.tones.filter((t) => !isNearNeutralHex(t.hex));
  if (from && to && colours.length === 1 && isNearNeutralHex(from) && isNearNeutralHex(to)) {
    const colour = normalizeHex(colours[0].hex);
    if (readsOn(to, colour)) return { allowed: true, why: 'neutral-swap' };
    return { allowed: false, why: 'neutral-swap-clashes' };
  }
  // -- exception 2: the mark is built of free-floating single-tone pieces --------------------
  const floating = floatingToneSet(mark);
  if (floating.length === 2) return { allowed: true, why: 'free-floating' };
  // His 3-tone extension is specified and unexercised - say so rather than guess (see the note).
  if (floating.length >= 3) return { allowed: false, why: 'free-floating-3tone-unbuilt' };

  /* ⭐ EXCEPTION 3 — THE NEUTRAL TOUCH (Ryan 2026-09-20, in his own words):
   *   *"If the colour being touched is a neutral, black or white, then we can change the neutral or
   *   change the colour — so long as the neutral a colour might be changed to isn't the same
   *   neutral as the one it is touching. Meaning the pink touching the black still allows us to
   *   change the pink, as long as it doesn't change to the same neutral as the black."*
   *
   * It asks about the colour BEING REPAINTED, not about the mark: repainting a colour that only
   * ever shares a shape with black or white cannot wreck anything, because the neutral is still
   * there to hold the drawing. Exception 2 was trying to ask this and could not — `inkPieces` drops
   * every shape under 5% of the mark and labels the survivors by their dominant tone, so Cherry
   * Valley's fifteen pink laurel leaves (biggest 2.1%) were discarded and the mark reported as one
   * colour. The pink was never judged attached; it was judged small. `measureToneTouch` is the same
   * flood without either of those, and this reads it.
   *
   * ⛔ THE "NOT THE SAME NEUTRAL" CLAUSE IS LOAD-BEARING AND MEASURED. It is the only thing sparing
   * three further plates he wants kept. Read as a RETARGET (send the pink to the other neutral
   * instead) rather than as a refusal, it reverses all three.
   *
   * ⭐ THE TWIN MERGE, his second sentence, rides on the same test: two of the mark's tones closer
   * than `TWIN_ON_PLATE_DELTA_E` count as ONE COLOUR — but only on a plate dealt in one of those
   * two colours, which is the scope he specified and it is what makes it safe. The swimmer's blue
   * wave is two cyans 18.1 apart, so the app saw the cyan "touching a real colour" when what it
   * touched was itself. ⛔ UNSCOPED IT IS STRICTLY WORSE AT EVERY BAR: at 16 it already reverses
   * buccaneersports twice to gain nothing.
   *
   * Corpus net, all 49 refused plates (WD 447, `probe-recolour-rules.py`): 12 freed — 5 he asked
   * for, 2 he was shown and traded away knowingly (umobilerams), 5 he had never seen. His nine hold.
   *
   * ⚠ IT SITS LAST ON PURPOSE, AND THAT IS A NO-OP TODAY. Exceptions 1 and 2 can each return a
   * REFUSAL, which would stop this one being reached — but no plate in the corpus currently exits
   * through either of those refusals (all 49 refusals are the plain `keep`), so the order changes
   * nothing now. It is written last because it is the broadest reading, and the narrow named
   * exceptions should keep their own answers when they have one. */
  const neigh = Array.isArray(mark.neighbours) ? mark.neighbours : [];
  if (from && to && neigh.length && (opts || {}).door !== 'band') {
    const entry = nearestToneEntry(neigh, from);
    /* ⛔ THE NEUTRAL TOUCH NEVER REPAINTS THE LOGO'S MAIN COLOUR (WD 470, pass 9). It fired on twelve
     * plates. On the six where the tone it moved was the mark's BIGGEST share, Ryan flagged all six:
     * db-logo's green (*"the logo used the correct shade of green in the before"*), 120087002997's red
     * (*"a black and white color scheme on the logo only which no longer matches the number"*) and
     * umobilerams' red. On the six where it moved a smaller tone (Cherry Valley's pink, the swimmer's
     * cyan, ahs-stadium's maroon, nmmi's white) he approved all six. The rule was written about a pink
     * laurel touching black lettering — an accent — and the main colour is the brand.
     *   Raw trace share, not a twin-merged one: the swimmer's two cyans merge to 0.49 against its black
     * 0.46 and that plate is one he approved. */
    const main = mark.tones.reduce((a, t) => ((t.share || 0) > (a ? a.share || 0 : -1) ? t : a), null);
    const self = nearestToneEntry(mark.tones, from);
    if (main && self === main) return { allowed: false, why: 'neutral-touch-main-tone' };
    if (entry) {
      const key = normalizeHex(entry.hex);
      const touches = (entry.touches || []).map(normalizeHex).filter(Boolean).filter((t) => {
        if (!colorsSameShade(key, t)) return true;                 // a different colour: it counts
        // his scope: the two only merge on a plate dealt in one of them
        return !(plate && (colorsIndistinguishable(plate, key) || colorsIndistinguishable(plate, t)));
      });
      if (!touches.length) return { allowed: true, why: 'neutral-touch-alone' };
      if (touches.every((t) => isGreyscaleHex(t))) {
        /* ⛔ "THE SAME NEUTRAL" IS A LIGHT/DARK QUESTION, NOT `isNearBlackHex`. That predicate is
         * r,g,b <= 24 - literal near-black - so a mid grey answers FALSE and then compares equal to
         * WHITE. The first cut of this used it and refused Cherry Valley's two plates, the very
         * ones Ryan asked for: their pink touches the black lettering AND its #373736 anti-alias
         * ridge, and that ridge read as "white-ish", which matched the white destination. */
        const darkish = (h) => relLuminance(h) < 0.5;
        const sameNeutral = touches.some((t) => darkish(t) === darkish(to));
        if (isGreyscaleHex(to) && sameNeutral) {
          return { allowed: false, why: 'neutral-touch-same-neutral' };
        }
        return { allowed: true, why: 'neutral-touch' };
      }
    }
  }
  return { allowed: false, why: 'keep' };
}

/** Channels this close together is a GREY — black, white, or anything on the line between them. */
const TOUCH_NEUTRAL_MAX_SPREAD = 18;

/**
 * Is this tone black, white, or a grey between them?
 *
 * ⛔ NOT `isNearNeutralHex`, WHICH IS A CHROMA BAR BUILT FOR THE RING and admits a pale tint. Ryan's
 * rule says *"if the colour being touched is a neutral, black or white"*, and #D7EAFF is a pale
 * BLUE — 40 apart across its channels — not a white. Built on the ring's predicate, the rule freed
 * three plates he wants kept (`secondary-png` twice, whose mark is white plus three pale blues, and
 * `drop-bear-logo` cfg2, whose greys are warm) because their tints answered "neutral". On the tones
 * that matter it is the anti-alias ridges that must still pass — Cherry Valley's #373736 sits at a
 * spread of 1 — so the bar is greyness, not lightness.
 */
function isGreyscaleHex(hex) {
  const h = normalizeHex(hex);
  if (!h) return false;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return Math.max(r, g, b) - Math.min(r, g, b) <= TOUCH_NEUTRAL_MAX_SPREAD;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 459, 2026-10-29) — "THE PINK PORTION TURNS WHITE".
 * Ryan on Cherry Valley v2 (pink + black, on the pink plate): *"on the pink plate the pink portion
 * changes to white."* The ladder proposed the mark's OWN BLACK instead (it is `safe` on that plate
 * and sits ahead of the neutral tail), which paints the whole mark one black tone. A target that
 * COLLAPSES the mark into BLACK is vetoed here, so the walk moves on to white.
 * ⛔ ONLY BLACK. Collapsing into WHITE is a plate he approves (garage-gym cfg1/cfg4, red + white on
 * the red plate, red -> white, 5 passes approved), and the wider readings were netted and died:
 *     any neutral the mark wears        7 move · 2 of his flags · 4 APPROVED broken
 *     any collapse into a neutral       4 move · 2 of his flags · 2 approved broken (garage-gym)
 *     collapse into BLACK only          2 move · 2 of his flags · 0 approved   <- this
 * (probe-neutral-retarget*.mjs over REVIEW-2026-09-23, 432 scoreable plates.) */
function collapsesIntoBlack(mark, fromHex, toHex) {
  const to = normalizeHex(toHex);
  const from = normalizeHex(fromHex);
  if (!to || !from || !isGreyscaleHex(to) || relLuminance(to) >= 0.5) return false;
  const rest = (mark && Array.isArray(mark.tones) ? mark.tones : [])
    .map((t) => normalizeHex(t && t.hex)).filter(Boolean)
    .filter((h) => !colorsIndistinguishable(h, from));
  return rest.length > 0 && rest.every((h) => isGreyscaleHex(h) && relLuminance(h) < 0.5);
}

/** The touch-map row for the tone nearest `hex` — the gate's `from` is a palette colour and the map
 *  is keyed on census tones, so they are the same ink under two names often enough to matter. */
function nearestToneEntry(neigh, hex) {
  const target = normalizeHex(hex);
  if (!target) return null;
  const rgbOf = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [r, g, b] = rgbOf(target);
  let best = null, bd = Infinity;
  for (const n of neigh) {
    const h = normalizeHex(n && n.hex);
    if (!h) continue;
    const [nr, ng, nb] = rgbOf(h);
    const d = (r - nr) ** 2 + (g - ng) ** 2 + (b - nb) ** 2;
    if (d < bd) { bd = d; best = n; }
  }
  return best;
}


/**
 * Would repainting tone `fromHex` to `toHex` still READ against what it sits beside — or fuse so
 * little of the mark that the repaint is still the better deal?
 *
 * ⛔ NOT the fusion test alone. Mission Patriots proved the first gap in one render: white→black
 * passes "would two touching regions end up the same colour" (no black in the mark), and the
 * patriot's face came back as black lines on a #101D41 head — 1.24:1, ΔE-distinct and unreadable,
 * WD 326's `colorsConflict`-is-not-a-legibility-test lesson exactly. So a touched tone offends
 * when it fails `readsOn` (the 2.0:1 bar 326 measured) against the proposed colour — which
 * subsumes fusion, since a colour against itself is 1:1.
 *
 * ⛔ AND NOT AN UNWEIGHTED VETO EITHER — RIGAR proved the second gap in the same corpus sweep. Its
 * black wordmark on the black plate has always been rescued to white (Ryan's own design: "extract
 * all the white… then invert it"); the letters' own white counters touch them, so a bare veto
 * refused the rescue and PEELED the wordmark — the whole mark invisible on the default plate, to
 * protect a few percent of counter ink. A band that merges with the plate is invisible either way;
 * the repaint RESTORES it, and what the veto is really weighing is the ink it would fuse against
 * the ink it would bring back. Hence the ink-share bar rather than any-offender-vetoes.
 * An absent table answers TRUE: unknown is not evidence, and the caller is only adding a safeguard.
 */
export function repaintReads(touches, fromHex, toHex) {
  const to = normalizeHex(toHex);
  if (!to) return false;
  let fusedInk = 0;
  for (const c of touchedTones(touches, fromHex)) {
    if (!readsOn(c.hex, to)) fusedInk += c.inkShare || 0;
  }
  return fusedInk < FUSE_MAX_INK_SHARE;
}

/** A trace tone must own this share of the ink before it can veto a repaint — below the table's
 *  3% floor (that is the point), above most anti-aliasing ridges. CSI's thin black band measures
 *  ~1–2%; a phantom blend row can only ever REFUSE a repaint, which falls to the add-ring. */
export const TRACE_VETO_MIN_SHARE = 0.004;
/**
 * The KENYON TEST, WD 337 (Ryan's corpus review, 2026-08-24): *"the logo doesn't have any black
 * in it so the black which spreads from the outline into the body doesn't conflict with
 * anything"* — a repaint target the mark ALREADY WEARS is only safe when the adjacency table can
 * actually weigh the fusion. For a tone the table records, `repaintReads` measures the real ink
 * at stake (RIGAR's counters, the Wallabies' yellow — both measured keeps). But thin detail under
 * the table's 3% floor is invisible to that veto — CSI's black keyline fused at a measured
 * fusedInk of 0.000 — so a target that Delta-E-matches a CENSUS tone with NO table row is
 * unverifiable, and unverifiable means NO: the reversible add-ring carries the contrast instead.
 * Absent census (older save) → false: unknown is not evidence, same convention as the table.
 */
export function unmeasuredPresence(structure, toHex) {
  const ls = structure || {};
  const to = normalizeHex(toHex);
  const trace = Array.isArray(ls.traceTones) ? ls.traceTones : null;
  if (!to || !trace) return false;
  const tableKeys = Object.keys(ls.toneTouches || {}).map(normalizeHex).filter(Boolean);
  for (const t of trace) {
    if ((t.share || 0) < TRACE_VETO_MIN_SHARE) continue;
    const hx = normalizeHex(t.hex);
    /* ⚠ BOTH OF THESE ARE CENSUS-ROW LOOKUPS, AND THE SECOND ONE IS WD 424's THIRD IDEA OF "the
       same ink" IN PERSON — it joins the TRACE census to the TOUCH census, two tables built from
       the same pixels that name a colour differently. At ΔE 14 a trace tone landed on the wrong
       touch row and the veto read as measured when it was not. See `sameToneRow`. */
    if (!hx || !colorsConflict(hx, to)) continue;                    // not this target's tone
    if (!tableKeys.some((k) => colorsConflict(k, hx))) return true;  // present, unmeasurable
  }
  return false;
}

/**
 * The options `bandConflictView` needs, built from a profile — ⭐ ONE definition, because this exact
 * five-key literal was hand-copied at SEVEN call sites (composite, the live raster sync, the panel,
 * `assignOutlineColors`'s band echo, `bandsVisibleOnPlate`, `presentedOuterOutline`, the corpus
 * runner) and the project's own §0b rule says that is how two places end up with different answers.
 * It is also what makes `ringed` below safe to add: a new key lands at every call site at once
 * rather than at the six somebody remembered.
 */
export function bandViewOptions(profile) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo');
  const aoRaw = Array.isArray(logoEl && logoEl.logoAddOutline)
    ? logoEl.logoAddOutline
    : ((logoEl && logoEl.logoAddOutline) ? [logoEl.logoAddOutline] : []);
  return {
    roles: bandRescuePool(p),
    avoid: ls.fillHex || (p.palette && p.palette.primary),
    touches: ls.toneTouches || null,
    touchesFill: ls.bandTouchesFill || null,
    trace: ls.traceTones || null,
    ringed: aoRaw.some((a) => a && normalizeHex(a.color) && a.thickness > 0),
    /* The mark facts `markRecolourAllowed` weighs - carried through this literal rather than read
       from a profile inside `bandConflictView`, because that function is deliberately profile-free
       (it takes bands + a plate + options, which is what makes it testable in node). This is also
       exactly the key-lands-at-every-call-site-at-once reason this builder exists. */
    mark: markToneRecord(p),
  };
}

export function bandConflictView(effBands, plateHex, { roles = [], avoid = null, touches = null, touchesFill = null, trace = null, ringed = false, mark = null } = {}) {
  /* `kept` = band indices that HAD a rescue and were refused it by Ryan's keep-the-mark gate. It is
     not a third outcome in the raster (a kept band is drawn exactly as it was drawn); it exists so
     `syncLogoContrastRing` can see that this plate was deliberately left merging and put the ring
     there. ⛔ Without it the rule is a net LOSS on any mark whose other rescue paths bail — measured
     on cherry-valley cfg1/cfg4, where `interiorMerges` bails on `fillUniform` and the plate came
     back with neither the recolour nor a ring. */
  const out = { repaint: {}, peel: [], kept: [] };
  /* ⚠ ILLUSTRATION ESCAPE HATCH — `app/tools/probe-keep-mark-pair.mjs` ONLY (WD 440), and the twin
   * of `contextualFillSwap`'s. Ryan has asked three times for the same alternative — *"keep the
   * logo outline/blue the same, then just add a 2nd white outline"* — and a mark's tone is
   * recoloured through TWO doors, so suppressing only the swap shows him an unchanged picture on
   * half the plates he flagged (tigers cfg1 and blazers cfg3 are repaints, not swaps).
   *   It returns the EMPTY view rather than "repaint nothing, peel as usual": the option he
   * described is the artwork untouched, and a peel removes the band as surely as a repaint
   * recolours it. Read off `globalThis`, set by that probe through `addInitScript` before the app
   * boots, and written by no shipped code path. */
  if (globalThis.__wdNoBandRepaint) return out;
  const n = conflictPeelCount(effBands, plateHex);
  if (n <= 0) return out;
  // The Kenyon test rides beside the fusion veto (WD 337) — see `unmeasuredPresence`.
  const tls = trace ? { traceTones: trace, toneTouches: touches } : null;
  const rescue = new Map(
    fillContrastRemap([...roles, ...effBands], plateHex, {
      allowDark: true,
      avoid,
      // A target that cannot READ against the tones this band touches in the artwork would fuse
      // or mud two regions — the ladder walks past it, and a band with no surviving target falls
      // through to `peel` below, which shows the plate through: visually what a plate-merging
      // tone already was.
      validate: (touches || tls)
        ? (from, to) => (!touches || repaintReads(touches, from, to)) && !unmeasuredPresence(tls, to)
        : null,
    })
      .map((sw) => [normalizeHex(sw.from), normalizeHex(sw.to)]),
  );
  for (let i = 0; i < n; i += 1) {
    /* ⛔ A DETACHED KEYLINE IS LEFT ALONE — neither repainted NOR peeled (Ryan 2026-08-18, the
     * Hanover white plate, in two rounds). A band standing OFF its fill — `bandTouchesFill`
     * false, the gap marks — separates nothing; when it merges with the plate, going subtle IS
     * the brand's own look there, and the mark still reads (fill + moat).
     *   Round one: the rescue repainted the white keyline BLACK — a colour the brand never had,
     * exterior only (the border flood cannot reach the interior fragments), mismatched halves of
     * one ring. Round two: PEELING it instead left a dotted halo — the keyline's edge pixels are
     * blends toward the keyed-out navy field, outside `borderFloodPeel`'s tolerance, so the core
     * vanished and the fringe stayed ("the exterior logo outline looks super broken"). KEEP has
     * neither failure: no invented colour, no raster surgery, and un-dyeing needs no undo.
     *   The bands that must KEEP THEIR REPAINT — Cherry Valley's black-on-black → white, RIGAR's
     * inversion, E Logo's ring — all TOUCH their fill: structural, and still rescued below. Same
     * measured fact that drives the text's gap ring; absent (older save) → old behaviour. */
    if (Array.isArray(touchesFill) && touchesFill[i] === false) continue;
    const to = rescue.get(normalizeHex(effBands[i]));
    /* ⛔ DOOR ONE OF TWO — see `markRecolourAllowed`; the twin call is in `contextualFillSwap`.
       ⚠ A REFUSED RESCUE KEEPS THE BAND. It does NOT fall through to the peel below, and the
       difference is the whole point: *"keep the logo outline/blue the same, then just add a 2nd
       white outline"* is the artwork UNTOUCHED, and a peel removes the band as surely as a repaint
       recolours it. Keeping it also arms the ring by itself — `repaint` stays empty, so
       `bandsVisibleOnPlate` goes false and `outerMerges` reads the DRAWN band against the plate,
       which is the merge it was written to catch. */
    /* ⛔ THE BAND DOOR DOES NOT GET EXCEPTION 3, AND THAT IS MEASURED, NOT TIDINESS. Ryan
       approved the neutral-touch rule against the plates the FILL SWAP refuses - 11 of them,
       with the two umobilerams reversals named and accepted. Let it rule the band door too and
       it frees 5 more plates he wants kept, one of which is `athletics-gcc-edu` cfg1 - one of
       HIS NINE, so the regression that governs this whole rule drops to 8 of 9. The band door
       is a separate question and it has not been put to him. */
    if (to && (!mark || markRecolourAllowed(mark, effBands[i], to, plateHex, { door: 'band' }).allowed)) { out.repaint[i] = to; continue; }
    if (to) { out.kept.push(i); continue; }
    /* ── R11 (Ryan's pass-one review, 2026-09-13): DO NOT THROW THE MARK'S OWN EDGE AWAY AND THEN
     * DRAW A NEW ONE AROUND WHAT IS LEFT. He wrote the remedy himself, three times, about three
     * different marks, and every time it was the same sentence:
     *     120018000234 cfg2  *"it should have KEPT the white outline around the logo, and THEN
     *                         added a 2nd outline which was black or blue."*
     *     benuredhawks cfg3  *"It should have just kept the regular outline, then added another
     *                         white outline around it to match the weight number."*
     *     blazers-belhaven   *"a better solution would have been to add a 2nd outline around the
     *                         white outline so the logo doesn't break."*
     *   The peel is the right answer on its own: a band that merges with the dye is invisible, and
     * showing the plate through is what it already looked like. It stops being the right answer the
     * moment a contrast RING is drawn afterwards, because `addOutlineDataUrl` follows the ALPHA
     * silhouette — peel the keyline and the ring lands directly against the mark's interior, which
     * is exactly the break he described: *"there's some black internal parts of the logo that the
     * new black outline now touches, breaking the logo."* Kept, the invisible band is still a gap,
     * and the mark reads.
     *
     * ⚠ SCOPED TO "A RING IS ACTUALLY THERE", NOT TO THE OUTERMOST BAND IN GENERAL. Measured over
     * all 548 plates of the 2026-09-15 corpus run: 25 plates peel their outermost band, and only
     * FOUR of them also carry an added ring — three are Ryan's own flags (the two above plus
     * gclancers cfg2) and the fourth is cvcc cfg1, which he approved and which this moves. Never
     * peeling the outermost band instead would move 19 more, Abbey and Malone among them — both
     * named hard controls. The narrow rule is the measured one.
     *
     * ⛔ ONLY THE OUTERMOST. An inner keyline that merges has the outer band, not the ring, beside
     * it; keeping it there changes nothing the ring can see. */
    if (i === 0 && ringed) continue;
    out.peel.push(i);
  }
  return out;
}

/**
 * Are the mark's outline bands VISIBLE on this plate — as drawn, or via a contextual repaint?
 * `null` when the mark has no detected bands (the question does not apply — CSI-class badges keep
 * their own ring rules); `true` when any band clears the plate or gets contextually repainted;
 * `false` when every band merges and stays (a detached keyline kept-but-invisible) or peels.
 *
 * The per-PLATE face of WD 325's mirror rule (Ryan 2026-08-18, the Hanover white plate): the mark
 * presents as bare red letters there — its detached keyline is kept but invisible — while the
 * number wore a BLACK ring `assignOutlineColors` derived because white failed the plate and navy
 * failed the fill. "The type wears what the mark wears" has to mean what it VISIBLY wears on the
 * plate in front of you. Consumed as `palette.logoBandsVisible` by the render-palette builders;
 * `effectiveTextStyle` drops non-locked rings when it is `false`.
 */
export function bandsVisibleOnPlate(profile, plateHex) {
  const p = profile || {};
  const ls = p.logoStructure;
  const bands = ls && Array.isArray(ls.bands) ? ls.bands.map(normalizeHex).filter(Boolean) : [];
  if (!ls || ls.confidence !== 'high' || !bands.length) return null;
  const plate = normalizeHex(plateHex);
  if (!plate) return true; // no plate to merge with → the bands are as drawn
  if (bands.some((h) => !colorsConflict(plate, h))) return true;
  const view = bandConflictView(bands, plate, bandViewOptions(p));
  return Object.keys(view.repaint).length > 0;
}

/**
 * ⭐ WHAT THE PLATE SHOWS FIRST ON THE MARK — its OUTERMOST presented OUTLINE, or `null` when the
 * mark wears none here. Ryan's criterion, 2026-09-15, is one sentence and this is its left-hand
 * side:
 *
 *   > **The outermost outline of the MARK and the outermost outline of the NUMBER must read as the
 *   > same colour.**
 *
 * Every "wrong" he gave on `RING-QUESTION.html` is that sentence — benu, valley, wallabies,
 * 120018000234, malone — and the one he called right is it too: *"The logo has a white outline and
 * so does the weight number - they both look good."*
 *
 * PRESENTED, never drawn. A band that merges with this plate is repainted or peeled by
 * `bandConflictView` before the buyer ever sees it, so reading `logoStructure.bands` directly would
 * answer with a colour that is not on the plate. Same trap, same day, in two other places
 * (`corpus-sheet-runner`'s dump and R10's first cut): `repaint`/`peel` are keyed by band INDEX.
 *
 * ⛔ IT IS AN **OUTLINE**, AND A MARK WITHOUT ONE ANSWERS `null` RATHER THAN OFFERING ITS FRONTIER
 * TONE. Measured over all 548 plates of the 2026-09-15 corpus run: treating a band-less mark's own
 * silhouette tone (`wrapTone` / the dominant frontier tone) as its "outermost outline" makes the
 * text mirror below fire on 29 plates Ryan approved to clear 10 of his flags — 17 of those 29 breaks
 * come from that one substitution. And it is the wrong ACTION for those marks anyway: on
 * athletics-umdearborn he asked for *"make the weight number yellow as well to match the logo"* —
 * the FILL, not a yellow ring around a navy number. That is cause B's own rule (one ink decision,
 * two surfaces) and it is a different build; this one stays honest about what it can see.
 *
 * @returns {{hex:string, weight:number}|null}  `weight` in TEXT-RING units — the add-ring's own
 *   stored fraction of the logo box, or (for a band, which carries no absolute weight) the mark's
 *   measured linework. A proportion of the MARK in both arms, never a taste constant.
 */
export function presentedLogoStack(profile, plateHex) {
  const p = profile || {};
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo);
  const out = [];
  if (!logoEl) return out;
  /* 1. AN ADDED / RESCUE RING WRAPS EVERYTHING. `logo-composite` applies `logoAddOutline`
        inner→outer in array order, so the LAST one is the edge the plate meets. Its thickness is
        stored as a fraction of the logo box, which is the same currency `outlineThicknessFor`
        trades in — multiply by the box and it is a text-ring weight. */
  const aoRaw = Array.isArray(logoEl.logoAddOutline)
    ? logoEl.logoAddOutline
    : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
  const adds = aoRaw.filter((a) => a && normalizeHex(a.color) && a.thickness > 0).slice(0, OUTLINES_MAX);
  for (let i = adds.length - 1; i >= 0; i -= 1) {
    out.push({ hex: normalizeHex(adds[i].color), weight: Number(adds[i].thickness) * LOGO_BOX_NOMINAL_VB });
  }
  /* 2. ELSE THE OUTERMOST BAND THAT STILL SHOWS HERE — the buyer's own overrides first (their
        recolour is what the raster paints), then the contextual view, walking outer→inner past
        anything peeled or switched off until one survives. */
  const ls = p.logoStructure || {};
  const raw = Array.isArray(ls.bands) ? ls.bands.map(normalizeHex) : [];
  /* WD 474 - A BAND-LESS MARK'S WRAP, RINGED, IS PART OF ITS EDGE. dscc's white keyline is no band (bands []),
     so this stack used to end at the added red ring and the number wore red alone. Ryan, shown the logo
     ringed red outside its white keyline: *"shouldn't the 25 LB now get a white outline with the red outline
     too?"* When a band-less mark carries an added ring and its wrap holds essentially the whole frontier
     (`WRAP_FRONTIER_OWN_MIN`, the wrap detector's own monopoly bar), the wrap sits between the ink and
     that ring, so it is entered inside it - even plate-coloured, where it reads as the gap it is. */
  if (!raw.length && adds.length && !globalThis.__wdNoWrapInStack) {
    const wrap = normalizeHex(ls.wrapTone);
    if (wrap && frontierShare(ls.toneTouches, wrap) >= WRAP_FRONTIER_OWN_MIN
      && colorsConflict(wrap, normalizeHex(plateHex)) && !colorsConflict(wrap, out[out.length - 1].hex)) out.push({ hex: wrap, weight: wrapEdgeWeight(p, ls, wrap, plateHex) });
  }
  if (ls.confidence !== 'high' || !raw.length || raw.some((h) => !h)) return collapseStack(out, plateHex);
  const ov = (logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') ? logoEl.logoBandColors : {};
  const effBands = raw.map((c, i) => normalizeHex(ov[i]) || c);
  const off = new Set((Array.isArray(logoEl.logoBandsOff) ? logoEl.logoBandsOff : []).map((i) => i | 0));
  const view = bandConflictView(effBands, normalizeHex(plateHex), bandViewOptions(p));
  /* ⭐ WD 470 — A BANDED MARK'S OUTER KEYLINE (Ryan 2026-10-30: *"yes, always match the outline"*).
     `analyzeStructure` keeps the tone that wraps everything OUT of `bands`, so hclarks (white keyline
     round red and gold bands) reported its red band as the outermost edge while the plate showed white.
     Narrow on purpose — the unscoped reading (any wrap, any mark) was netted at 40 approved plates
     broken (`probe-wrap-net.mjs`): it ONLY applies to a high-confidence banded mark, ONLY when the wrap
     is a colour none of its bands wear (lagrange's wrap IS its inner band and came out as a third ring),
     and ONLY when it READS on this plate (`colorsConflict`, not `colorsIndistinguishable`: CSI's #1B1818
     keyline on the #0B0C0F plate is not the same ink and is still invisible, on the logo as on the number).
     A band-less mark's wrap is often its silhouette fill
     and is not touched here. */
  let wrapPushed = false;
  if (!globalThis.__wdNoWrapInStack && !adds.length) {
    const wrap = normalizeHex(ls.wrapTone);
    const plate = normalizeHex(plateHex);
    /* ⛔ AND IT MUST BE THE EDGE — hold most of the mark's background frontier (WD 470, the render).
       Ryan flagged the ring on body-state (wrap holds 0.30 of the frontier) and ee-logo (0.21) as "an
       extra outline"; he kept it on hclarks and nccspartans (1.00), ncurams (0.78) and CSI (0.86). The
       touch table's own frontier ownership — `wrapToneFromTouches`' monopoly measure — separates them
       with nothing in between; the bar is "most of the edge", not a fitted number. */
    if (wrap && !effBands.some((b) => colorsConflict(b, wrap)) && !(plate && colorsConflict(wrap, plate))
      && frontierShare(ls.toneTouches, wrap) > 0.5) {
      out.push({ hex: wrap, weight: wrapEdgeWeight(p, ls, wrap, plateHex) });
      wrapPushed = true;
    }
  }
  for (let i = 0; i < effBands.length; i += 1) {
    /* ⭐ WD 470 — A BAND THE PLATE SWALLOWS, WITH AN EDGE STILL OUTSIDE IT, IS A STRIPE OF PLATE.
       hclarks on its red plate shows white keyline > RED > gold, the red being the plate seen through
       where the peeled band was; nccspartans on black shows the same with black. Skipping it put the
       number's gold directly under its white (*"the yellow inner outline is the issue... 19 and 20 get
       the outlines correct"*). Kept as a plate-coloured entry ONLY when the keyline above (1b) sits
       outside it. ⛔ "anything visible outside it" was netted at 68 plates / 56 approved broken: on every
       other mark the number already handles that gap its own way (`withGap`, the moat), and this doubled it. */
    /* ...and only SANDWICHED: a visible band must sit inside it too. The innermost band in the plate's
       colour is the gap the number already shows with its own moat (hclarks on gold, nccspartans on
       orange — both of which he called right); stacking a second one there doubled it. */
    const swallowedAt = (k) => (view.peel || []).includes(k)
      || colorsIndistinguishable(normalizeHex(view.repaint[k]) || effBands[k], normalizeHex(plateHex));
    const visibleInside = (k) => effBands.some((_, m) => m > k && !off.has(m) && !swallowedAt(m));
    const swallowedInside = (k) => !globalThis.__wdNoMoatInStack && wrapPushed
      && swallowedAt(k) && visibleInside(k);
    if (!off.has(i) && swallowedInside(i)) {
      const fracs0 = Array.isArray(ls.bandWidthFracs) ? ls.bandWidthFracs : null;
      const own0 = fracs0 ? ringWeightFromFrac(fracs0[i]) : null;
      out.push({ hex: normalizeHex(plateHex), weight: own0 == null ? outlineThicknessFor(p) : own0 });
      continue;
    }
    if (off.has(i) || (view.peel || []).includes(i)) continue;
    const hex = normalizeHex(view.repaint[i]) || effBands[i];
    /* ⛔ A BAND THE PLATE SWALLOWS IS NOT THE MARK'S OUTERMOST OUTLINE, AND THIS WALKED PAST A
     * PEELED BAND BUT NOT PAST AN INVISIBLE ONE (WD 453). `bandConflictView` peels or repaints a
     * band that CONFLICTS with the plate, but its keep-the-mark gate can deliberately leave one
     * merging (WD 440b) and a plate dyed from the mark's own tone lands on one by construction —
     * so this loop could return a band drawn in the plate's own colour, which the buyer sees as
     * bare rubber. Measured over all 528 plates: 15 do exactly that, and on every one of them
     * there is a visible band one step in that this loop never reached.
     *   What it cost: `mirrorOuter` then asks `readsOn(plate, tone)` of a tone that IS the plate,
     * gets a contrast ratio of 1.0, and refuses to mirror anything at all — so the number wore no
     * mirrored ring on 15 plates where the mark visibly wears one. depauwtigers cfg3 is the plate
     * Ryan judged: white-on-white was named, the black band underneath it never was, and he
     * pressed *"Add it - match the mark"* on the result.
     *   ⚠ SAME TEST THE MARK STACK USES, deliberately — `colorsIndistinguishable` is "these are
     * the same ink", not `colorsConflict`'s "these read as one edge". A band merely CLOSE to the
     * plate is the band view's business and it has already had its say by the time we get here;
     * widening this to `colorsConflict` would second-guess a repaint the view chose to keep. */
    if (hex && colorsIndistinguishable(hex, normalizeHex(plateHex))) continue;
    /* ⭐ A BAND CARRIES ITS OWN THICKNESS NOW (WD 438) — and until it did, this had to answer with
       the mark's MEDIAN stroke instead. `analyzeStructure` normalises a mark's bands so their median
       lands on 2.5, which keeps the ratios BETWEEN one mark's bands and throws the absolute weight
       of its linework away, so `thickness` was never spendable here. `widthFrac` is that band's own
       medial-axis width, measured by the same pass that measures `logoStrokeFrac` and priced in the
       same currency — a fraction of the artwork's long edge — so it converts identically.
         This is the right-hand half of Ryan's own criterion: the number's outermost ring should read
       as the mark's outermost EDGE, and an edge has a weight as well as a colour. Six of his eight
       weight notes are about a mirrored ring (*"the thickness of the outline on the number should
       match the logo as best as possible"*), and mirroring the median stroke answers a question
       about the WHOLE MARK when the thing being mirrored is one band of it.
         ⚠ The fallback is unchanged and still the honest one: a band with too few ridge samples to
       measure, or an older save with no `bandWidthFracs`, gets the mark's measured linework — never
       a constant, which is the half of Ryan's *"very big and bulky… a big no no"* that a mirrored
       ring could otherwise re-create. */
    if (hex) {
      const fracs = Array.isArray(ls.bandWidthFracs) ? ls.bandWidthFracs : null;
      const own = fracs ? ringWeightFromFrac(fracs[i]) : null;
      out.push({ hex, weight: own == null ? outlineThicknessFor(p) : own });
    }
  }
  return collapseStack(out, plateHex);   // empty = every band peeled or switched off; it presents bare
}

/* ⭐ THE WRAP'S OWN WEIGHT (WD 476). Ryan on svsucardinals: *"copied white ring is heavier than the
   logo's hairline"* - and shown it at 1 and 0.5 beside ncurams (the same 1-px white hairline outside a
   navy keyline), *"I want the right column"* on BOTH. The wrap used to borrow the mark's median stroke;
   `toneTouches[wrap].edgeFrac` (logo-structure.js `edgeRunFracs`) is its width where it faces the
   background (a hairline: see `WRAP_HAIRLINE`), and it only ever THINS:
   an edge measuring wider than the median stroke keeps the median, so no ring gets heavier.
   ⛔ A general 0.5 floor was netted first: 151 plates / 147 approved, and svsu's white did not move.
   ⛔ AND ONLY AN EDGE THAT IS DRAWN. Where the wrap is the plate's own colour it enters the stack as the
   GAP between number and rubber (WD 474, dscc: *"shouldn't the 25 LB now get a white outline..."*), and
   thinning it there moved 10 approved plates of 7 marks that are not what he asked about. */
/* ⭐ A HAIRLINE IS DRAWN AT 0.3 (Ryan on the 0.5 render: *"0.5 might be a bit too thick and these need to
   go down to 0.2 or 0.3 or 0.4"*). ⛔ The measurement cannot pick inside that range: both marks' white edge
   is a ONE-PIXEL run at the analysis size, which says "at most a pixel" - svsu reads 0.53u live (small
   analysis canvas) and 0.26u on its 200-px file, ncurams 0.28u. So an edge under `WRAP_HAIRLINE_BELOW`
   (exactly the edges the 0.5 grid already thinned: svsu, ncurams) takes the middle of his range, and
   every other edge keeps the 0.5 grid. ⛔ Rounding all sub-1 edges to 0.1 was netted: it also moved
   nccspartans 4 (1 -> 0.8) and CSI 1/2/4 (1 -> 0.9), all approved. */
export const WRAP_HAIRLINE = 0.3;
export const WRAP_HAIRLINE_BELOW = 0.75;
function wrapEdgeWeight(p, ls, wrap, plateHex) {
  const base = outlineThicknessFor(p);
  const plate = normalizeHex(plateHex);
  if (plate && colorsConflict(wrap, plate)) return base;
  const t = ls && ls.toneTouches;
  let frac = null;
  if (t && typeof t === 'object') {
    for (const k of Object.keys(t)) {
      if (normalizeHex(k) === wrap || sameToneRow(k, wrap)) { frac = t[k] && t[k].edgeFrac; break; }
    }
  }
  if (!(frac > 0)) return base;
  const units = frac * LOGO_BOX_NOMINAL_VB;
  const w = units < WRAP_HAIRLINE_BELOW ? WRAP_HAIRLINE : Math.round(units * 2) / 2;
  return Math.min(base, w);
}

/** adjacent duplicates are ONE edge, not two - `colorsConflict` is the app's own "reads as one" test */
function collapseStack(list, plateHex) {
  void plateHex;
  return list.filter((b, i) => i === 0 || !colorsConflict(b.hex, list[i - 1].hex));
}

/**
 * WHAT THE PLATE SHOWS FIRST ON THE LOGO - the head of `presentedLogoStack`, and nothing else.
 *
 * /!\ ONE ANSWER, ONE PLACE. This used to be the whole function and the stack was a reimplementation
 * of it in a probe; WD 453 is the session where that reimplementation read the logo one ring short
 * on the 162 configs that wear a tool-added ring, and shipped a decision page on it. Deriving the
 * singular FROM the plural is what stops the two ever disagreeing again.
 */
export function presentedOuterOutline(profile, plateHex) {
  const s = presentedLogoStack(profile, plateHex);
  return s.length ? s[0] : null;
}

/**
 * The contextual fill swap for an OUTLINE-LESS mark: a brand colour that merges with the plate is
 * repainted to the standout brand colour (green→yellow on a green plate). Same extraction reason as
 * `bandConflictView` — the swatch has to show what the raster will do.
 *
 * Gated exactly as the raster gates it: a BANDED mark's interior is protected by its kept inner ring
 * and the band pass already handles the outer conflict; a manual flatten is the buyer's own choice;
 * and a `colorBorneDetail` mark is one whose detail IS its colours, where repainting one tone to
 * another already present merges two adjacent regions into one shape.
 *
 * ⛔ THE NEAR-BLACK BAIL IS GONE HERE TOO (Ryan 2026-08-13) — *"on a black plate, the black parts of
 * this logo should turn white like they used to do… but they stay black and blend in with the plate
 * so you can't see them."*
 *
 * 🔑 ONE WRONG PREMISE, AND THIS IS THE **THIRD** FILE IT HAS LIVED IN. `fillContrastRemap` returns
 * `[]` for a near-black base unless `allowDark`, on the reasoning that black is the undyed default and
 * therefore "cannot bite". It is the plate that bites HARDEST — every near-black ink vanishes into it.
 * That was corrected for the TEXT in `assignOutlineColors` (2026-08-05) and for the LOGO'S BANDS in
 * `conflictPeelCount` (2026-08-10, this same crest), and **this call was left behind both times**
 * because a band-less mark on a black plate had never been the case in front of anyone.
 *
 * ⚠ WD 288 IS WHAT PUT A MARK HERE. Cherry Valley used to be (wrongly) classified as fill + band, so
 * the BAND path rescued its black and Ryan saw white. Correcting the classification moved it onto
 * this path — which still carried the un-corrected premise — so a fix in one file silently undid a
 * fix in another. A behaviour that survives only because a mark takes the wrong branch is not a
 * working behaviour; it is a second bug cancelling the first.
 *
 * ⚠ `avoid` IS WHAT MAKES THE ANSWER WHITE RATHER THAN ROSE — AND IT IS SCOPED TO THE NEAR-BLACK
 * PLATE, because applying it everywhere trades this bug for a worse one. Without `avoid`,
 * `fillContrastRemap` sends the conflicting tone to the mark's other BRAND colour; Cherry Valley's
 * only non-conflicting tone is its rose, so wreath, monogram and both wordmarks would all turn rose
 * and the two-tone badge would flatten to one colour on the DEFAULT plate. With `avoid` set to the
 * fill, that target is filtered out, the search falls through to the neutral furthest from the base,
 * and the answer is white — what `bandConflictView` produces three lines up, and what Ryan had
 * before 288 moved this mark onto this path.
 *
 * ⛔ APPLYING `avoid` ON A DYED PLATE BREAKS THIS FUNCTION'S OWN DOCUMENTED CASE. Measured on the
 * Wallabies (roles `#F0AF00` yellow / `#1C6648` green): on a GREEN plate the conflicting tone is the
 * green, `avoid` is the yellow fill, and filtering the yellow out leaves nothing — so
 * "green→yellow on a green plate", the example in this very docstring, became green→WHITE. A house
 * neutral replacing a brand colour is exactly what `bestContrastRef` calls a last resort.
 *   So `avoid` is passed only when the base is near-black, which is the case that has a settled
 * answer everywhere else in the app (`assignOutlineColors`, `bandConflictView`): dark ink on the
 * undyed plate goes to the neutral. Every DYED-plate answer is therefore byte-identical to before —
 * verified: Wallabies on yellow → green, on green → yellow; Cherry Valley on rose → black.
 *   ⚠ `isNearBlackHex`, the same predicate `fillContrastRemap` bails on, so the scope of the
 * exception is exactly the scope of the bail it is correcting. Deriving it here with a second
 * near-black test would be two definitions of one line (CLAUDE.md §0b).
 *
 * ⚠ RETURNS BOTH HALVES (WD 440b): `{ allowed, refused }` — the swaps this plate still gets, and the
 * ones Ryan's keep-the-mark gate declined. `contextualFillSwap` is `.allowed` and is what every
 * caller but one wants; `syncLogoContrastRing` needs `.refused`, because a mark deliberately left
 * merging is a mark that now needs the ring he asked for in the recolour's place, and no other
 * trigger in that function can see a rescue that was declined rather than absent.
 */
export function contextualFillSwapSplit(profile, plateHex) {
  /* ⚠ ILLUSTRATION ESCAPE HATCH — `app/tools/probe-keep-mark-variant.mjs` ONLY (2026-09-17).
   * Ryan, on umobilerams: "feel free to render a version of both examples I'm describing
   * side-by-side." One of his two examples is "keep the logo the exact same color", which is
   * precisely the ABSENCE of this function's output, and nothing else in the app can ask for that.
   * Read off `globalThis`, set by that probe through `addInitScript` before the app boots, and
   * written by no shipped code path. */
  if (globalThis.__wdNoContextualSwap) return { allowed: [], refused: [] };
  const p = profile || {};
  const bands = (p.logoStructure && Array.isArray(p.logoStructure.bands)) ? p.logoStructure.bands : [];
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo');
  const center = normalizeHex(logoEl && logoEl.logoCenterColor) || null;
  const colorBorne = !!(p.logoStructure && p.logoStructure.colorBorneDetail === true);
  /* ⛔ THE COLOUR-BORNE BLANKET GATE IS NOW CONDITIONAL ON THE TOUCH TABLE (Ryan 2026-08-18, BC
   * Tornados: fine navy+slate linework, and on the slate plate the slate half of the spiral
   * vanished — *"the logo turns super blurry"*. Half-vanished linework IS the blur). The gate's
   * reason was that repainting one tone of a colour-borne mark to another tone already present
   * merges two adjacent regions — which is EXACTLY what `repaintReads` (2a, the ink-weighted
   * touch veto) now tests per candidate, with measured adjacency instead of a blanket no. So:
   * a colour-borne mark WITH a `toneTouches` table gets the swap ladder, veto-guarded — a
   * destructive candidate is walked past, and no survivor means no swap, same as today. An older
   * save with no table keeps the blanket gate: unknown adjacency stays untouchable. */
  if (center) return { allowed: [], refused: [] };
  /* ── R6 (Ryan's pass-one review, 2026-09-13): A BANDED MARK'S INTERIOR IS NOT AUTOMATICALLY SAFE
   * ────────────────────────────────────────────────────────────────────────────────────────────
   * This bailed on `bands.length` outright, on the reasoning that *"a BANDED mark's interior is
   * protected by its kept inner ring and the band pass already handles the outer conflict."* That
   * is true of an interior the band WRAPS and false of one that reaches the edge, and Ryan flagged
   * three plates of the second kind:
   *     cherry-valley cfg1/4  *"The D7A8A3 section of the logo should have changed to white on this
   *                            plate."*  (its #D7A8A3 faces the background over 20% of its boundary)
   *     120051002169 cfg1     *"The blue part of the wave in the logo blends in with the plate
   *                            colour. It should probably turn black to match the swimmer."* (46%)
   *
   * ⭐ AND THE TEST THAT SEPARATES THEM FROM THE CONTROLS WAS ALREADY IN THIS FUNCTION, one line
   * down: `facesPlate`, the ECC Kats rule — *"ink the plate never shows beside cannot be
   * swallowed."* Measured on the 2026-09-15 corpus dumps, it splits Ryan's flags from the two marks
   * whose teal-on-teal and red-on-red he explicitly approved, with the whole of the open space
   * between 0.03 and 0.19 to spare:
   *     KEEP   e-logo  #047379 on its own teal plate   bgFace 0.000   (his protected control)
   *     KEEP   gcc     #EB1F25 on its own red plate    bgFace 0.000   ("the logo looks great")
   *     FIX    cherry-valley #D7A8A3                   bgFace 0.196
   *     FIX    2169    #6EC7DD                         bgFace 0.465
   *     FIX    cmlions #1E2F5D                         bgFace 0.953   ("big EXPOSED parts")
   * No new constant: `WRAPPED_TONE_BG_MAX` is the 2026-08-23 measurement, unchanged.
   *
   * ⚠ IT CANNOT REACH THE BANDS, WHICH IS WHY DROPPING THE GATE IS SAFE RATHER THAN BOLD.
   * `remapBandsFromImageData` applies `fillRemap` only where `labelBandsFromBorder` says the pixel
   * is NOT a band (`label[p] < 0`), so the band pass keeps sole ownership of band ink exactly as
   * the old bail intended — the bail was guarding a door the raster already locks. And it is
   * self-limiting: `fillContrastRemap` returns swaps only for tones that CONFLICT with the plate,
   * so every banded mark whose roles clear their plate is byte-identical.
   *
   * ⛔ BUT A GAP MARK KEEPS THE OLD BAIL, AND MEASURING IS THE ONLY REASON THAT LINE IS HERE.
   * Without it this moves **athletics-hanover-edu cfg1** — one of the two deliberate look-alike
   * keeps the audit protects by name. Hanover is red letters, a keyed-out moat, then a white
   * keyline: on its own red plate the letters face the background at 0.999 and merge with the
   * plate, which reads as a textbook case for this rescue and IS THE DESIGN. Ryan built that moat
   * himself (*"should we implement a forced blue outline followed by a white outline around the
   * text to match that same aesthetic?"*) — a plate-coloured gap is the look, so ink that reads as
   * the plate beside it is the look too. `logoGapFromStructure` is that fact and it already exists;
   * it also keeps Mount Mercy cfg1 (approved) still, and it declines cmlions cfg1 — which is right
   * for a second reason: Ryan asked for an OUTLINE there (*"should have just added an outline and
   * we would be good"*), not a recolour. See the changelog for why cmlions cannot yet be told apart
   * from Hanover on any measured property, and is therefore a question rather than a rule.
   *
   * Measured, whole corpus: clears cherry-valley cfg1 + cfg4 and 2169 cfg1 · moves cvcc cfg2 and
   * tigers cfg2 (both approved) · 6 never-judged plates · every named control byte-identical. */
  if (colorBorne && !(p.logoStructure && p.logoStructure.toneTouches)) return { allowed: [], refused: [] };
  if (bands.length && logoGapFromStructure(p.logoStructure)) return { allowed: [], refused: [] };
  const pal = p.palette || {};
  const onBlack = isNearBlackHex(normalizeHex(plateHex));
  // Same veto as the band rescue: a swap target that cannot read against the tones the source
  // touches would fuse or mud two regions. A dropped pair keeps its original colour — for a tone
  // that merges with the plate that is the look the mark already had, and fusing two of its
  // regions to "fix" it is strictly worse.
  const touches = p.logoStructure && p.logoStructure.toneTouches;
  /* ⛔ A WRAPPED tone is exempt (Ryan 2026-08-23, ECC Kats — see `bgFaceShare`): the swap exists
     to rescue ink the plate would swallow, and ink the plate never shows beside cannot be
     swallowed. Repainting it is pure artwork rewriting (red claws → white on the red plate,
     wrapped in their own dark outline the whole way round). Table absent → unknown → old
     behaviour, same convention as every touch-table consumer. */
  /* ⛔ CAUSE 3 IS NOT A BUG IN THIS LINE, AND FAILING IT CLOSED IS MEASURED AND REJECTED (WD 427).
     Ryan's audit filed it with causes 2/4/5 as one family — *"the wrapped-ink guard fails OPEN when
     it cannot find the tone; 120039004726 asked about #004900, which is in ZERO pixels"* — and the
     obvious repair is to read "the table has no such row" as an ANSWER (not in the artwork) rather
     than as ignorance. ⛔ IT DOES NOT SEPARATE. The `from` hex here comes from the PALETTE and the
     rows come from the RASTER, two quantisers that name one ink differently by construction (the
     same fact WD 424 found), so the join is lossy on plates Ryan APPROVED too. Measured, ΔE from
     each swap's `from` to its nearest census row:
         REFUSE   120039004726 cfg1   #004900 -> #006E00   ΔE 21.13   (his flag)
         KEEP     athletics-csi-edu   #FCF1C2 -> #FFFFFF   ΔE 24.86   approved
         KEEP     120081000896 cfg4   #7C84A0 -> #989797   ΔE 17.78   approved
         KEEP     ncurams-com cfg3    #A19B81 -> #B3A26A   ΔE 17.20   approved
         KEEP     angry-panda cfg2    #F0612A -> #FE550A   ΔE 14.66   approved
     The one he wants refused sits INSIDE the range of the four he approved, so there is no bar on
     this axis — the fail-closed version breaks 7 approved plates to clear 1 flag. ⭐ What 4726
     actually needs is a question about what the plate/artwork SHOULD do when the dealt plate is a
     snapped version of a tone the mark does not quite wear; it is not a threshold. Left open. */
  const facesPlate = (hex) => {
    if (!touches) return true;
    const share = bgFaceShare(touches, hex);
    return share === null || share >= WRAPPED_TONE_BG_MAX;
  };
  // The Kenyon test (WD 337): a swap target the mark already wears, that the table cannot
  // measure, is refused — the add-ring rescues instead. See `unmeasuredPresence`.
  const ls = p.logoStructure || null;
  /* A tone that IS one of the mark's bands is the BAND pass's business, never this one — the
     raster already refuses to repaint band pixels (`remapBandsFromImageData` skips `label >= 0`),
     so offering one here can only produce a swap that does nothing to the picture while changing
     what `syncLogoContrastRing` believes has been rescued. gcc's tertiary #ED1F25 and E Logo's
     rust are both bands and both approved on their own plate; this is what keeps them still. */
  const isBand = (h) => bands.some((b) => { const n = normalizeHex(b); return n && normalizeHex(h) && colorsConflict(n, h); });
  /* ⛔ THE INK THAT DISAPPEARS WITHOUT "CONFLICTING" (Ryan 2026-09-17, layton-high-school cfg3).
   * See `fillContrastRemap`'s `alsoRescue` note for why the conflict test alone cannot see this.
   * The gate is TWO conditions and both are measured, not chosen to fit:
   *   • the plate is NEAR-BLACK — this function's own docstring already says the black plate
   *     "bites HARDEST", and it is the scope `avoid` is already limited to two lines down, so the
   *     exception's scope stays the scope of the bail it corrects.
   *   • the vanishing tone is MOST OF THE MARK. A dark accent swallowed by a black plate is a
   *     detail; a dark tone that IS the mark is the mark disappearing, which is what Ryan is
   *     looking at. `MARK_LOST_INK_MIN` is that bar.
   * ⚠ BOTH ARE LOAD-BEARING, measured over all 528 plates of the 2026-09-17 render:
   *     ungated                      clears 11 / breaks 93   (moves Kenyon, E Logo, Malone, Wexmoor)
   *     near-black only              clears  7 / breaks 15   (moves KENYON — the gold control)
   *     near-black AND >= 60%        clears  6 / breaks  3   (no control moves)
   * ⛔ AND THE THRESHOLD-FREE VERSION IS WORSE, which is why there is a number here at all:
   * "rescue it when it is the mark's DOMINANT tone" measures clears 7 / breaks 9. The bar is doing
   * real work rather than dressing up a preference. */
  const lostInk = (hex) => onBlack
    && !readsOn(plateHex, hex)
    && (toneInkShare(p, hex) || 0) >= MARK_LOST_INK_MIN;
  /* ⛔ DOOR TWO OF TWO - see `markRecolourAllowed`. Filtered on the way OUT rather than gated on
     the way in, deliberately: `syncLogoContrastRing` asks this function "was this tone already
     rescued?" to decide whether to ring the mark, so a swap this gate refuses has to be absent from
     the ANSWER, not merely unapplied. That is the wiring that makes the refusal arm the ring. */
  const markRec = markToneRecord(p);
  const flat = oneColourFlatten(p, markRec, plateHex);
  if (flat) return { allowed: flat, refused: [] };
  const all = fillContrastRemap([pal.primary, pal.secondary, pal.tertiary].filter((h) => h && !isBand(h) && facesPlate(h)), plateHex, {
    allowDark: true,
    alsoRescue: lostInk,
    // Both house neutrals (see `fillContrastRemap`): there is no peel behind this call, so a
    // vetoed first neutral must not end the walk while the other one still reads.
    bothNeutrals: true,
    avoid: onBlack ? ((p.logoStructure && p.logoStructure.fillHex) || pal.primary) : null,
    validate: (touches || (ls && ls.traceTones))
      ? (from, to) => !collapsesIntoBlack(markRec, from, to)
        && (!touches || repaintReads(touches, from, to)) && !unmeasuredPresence(ls, to)
      : (from, to) => !collapsesIntoBlack(markRec, from, to),
  });
  return { allowed: all.filter((sw) => markRecolourAllowed(markRec, sw.from, sw.to, plateHex).allowed),
    refused: all.filter((sw) => !markRecolourAllowed(markRec, sw.from, sw.to, plateHex).allowed) };
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 479, 2026-10-31) — A ONE-COLOUR LOGO THE PLATE SWALLOWS GOES FLAT.
 * Ryan on Grand Hyatt (thin red lettering, on the red 45 and the maroon 35): *"I think the logo would look
 * better if we changed it to a flat white, instead of kept the red then tried to add a thin white outline
 * around an already thin/detailed logo."* Then: it needs a RULE, not the chooser.
 *   The gate refused it as `neutral-touch-main-tone` (WD 470: never repaint the logo's MAIN colour). That
 * clause protects a brand colour among OTHER colours (db-logo's green, 120087's red); on a logo that IS one
 * colour there is nothing for the repaint to break - the drawing survives whole, in white.
 *   "One colour" = every census tone >= 3% is a SHADE of the biggest one (`TONE_MERGE_DELTA_E`, the app's
 * own "two shades read as one") and none is black/white/grey. Measured over the 31r corpus (131 logos):
 * Grand Hyatt's shades sit 22.2 apart; the next logo with no neutral in it (body-state-u) is 57.9 apart -
 * two real colours, approved - so the bar is inside an empty gap. Scoped to a plate the colour does not
 * READ on (`readsOn`, the ring's own test): his approved white and black plates stay red.
 *   ⛔ Not a "thin" test: nothing in the corpus measures stroke width, and only this one logo is in scope. */
function oneColourFlatten(p, markRec, plateHex) {
  const plate = normalizeHex(plateHex);
  const tones = (markRec && markRec.tones) || [];
  if (!plate || !tones.length) return null;
  const main = tones.reduce((a, t) => ((t.share || 0) > (a.share || 0) ? t : a));
  const mainHex = normalizeHex(main.hex);
  if (!mainHex || tones.some((t) => isNearNeutralHex(t.hex)
    || deltaE(labOfHexLocal(t.hex), labOfHexLocal(mainHex)) >= TONE_MERGE_DELTA_E)) return null;
  if (readsOn(plate, mainHex)) return null;
  const to = contrastRatio(plate, '#FFFFFF') >= contrastRatio(plate, '#000000') ? '#FFFFFF' : '#000000';
  if (!readsOn(plate, to)) return null;
  /* Every name the one colour goes by: the palette's AND the census's. The painter already takes the census
     shades with the palette tone (all inside its radius), but `presentedToneShares` maps rows by name, so an
     unnamed shade stayed red in the bookkeeping and won the weight number on the maroon plate. */
  const pal = p.palette || {};
  const ls = p.logoStructure || {};
  const trace = (Array.isArray(ls.traceTones) && ls.traceTones)
    || (Array.isArray(p.logoTraceTones) && p.logoTraceTones) || [];
  const froms = [...new Set([
    ...(Array.isArray(pal.all) ? pal.all.map((x) => x && x.hex) : []),
    pal.primary, pal.secondary, pal.tertiary,
    ...trace.map((x) => x && x.hex),
    ...Object.keys((ls.toneTouches && typeof ls.toneTouches === 'object') ? ls.toneTouches : {}),
  ].map(normalizeHex).filter((h) => h && !isNearNeutralHex(h)))];
  // `flat` tells `syncLogoContrastRing` the logo already reads here, so it adds no ring around it.
  return froms.length ? froms.map((from) => ({ from, to, flat: true })) : null;
}
function labOfHexLocal(hex) {
  const h = normalizeHex(hex) || '#000000';
  return srgbToLab([1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));
}

/** The swaps that actually apply to this plate — `contextualFillSwapSplit`'s `.allowed` half, and
 *  the signature every caller but the ring has always used. */
/** WD 475: two palette tones closer than this are one ink - the census's own merge distance (palette-extract,
 *  `deltaE(y.lab, lab) < 10`). */
const SAME_INK_DELTA_E = 10;
/**
 * WD 475 - A REPAINT OWNS ITS OWN INK, NOT A NEIGHBOUR'S. The painter takes every pixel within `KEY_TOL2` (66 RGB)
 * of a swap's source, and on 120150001516 cfg2 the badge's light-grey field (#D1D2D4) sits 65 from the white
 * being swapped to black - so the white-to-black swap blackened most of the badge (Ryan: *"The black logo is
 * wrong. Should keep the original logo which is mostly white"*). Each swap now carries `keep`: the palette tones
 * inside that radius that are a DIFFERENT ink (>= `SAME_INK_DELTA_E`); a pixel nearer to one of them is that
 * tone's, and the swap skips it. Of the corpus's 30 swaps only three have another palette tone in the radius,
 * and the other two (carroll's #F9F4EC, elite-edge's #FBFCFC) are the same white re-quantised - unchanged.
 */
function withKeepTones(profile, swaps) {
  const p = profile || {};
  const pal = p.palette || {};
  const tones = [...new Set([
    ...(Array.isArray(pal.all) ? pal.all.map((x) => x && x.hex) : []),
    pal.primary, pal.secondary, pal.tertiary,
    ...(Array.isArray(p.swatches) ? p.swatches : []),
  ].map(normalizeHex).filter(Boolean))];
  const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return swaps.map((s) => {
    const f = normalizeHex(s && s.from);
    if (!f) return s;
    const fr = rgb(f), fl = srgbToLab(fr);
    const keep = tones.filter((t) => {
      const tr = rgb(t);
      const d2 = (tr[0] - fr[0]) ** 2 + (tr[1] - fr[1]) ** 2 + (tr[2] - fr[2]) ** 2;
      return d2 > 0 && d2 <= KEY_TOL2 && deltaE(srgbToLab(tr), fl) >= SAME_INK_DELTA_E;
    });
    return keep.length ? { ...s, keep } : s;
  });
}
export function contextualFillSwap(profile, plateHex) {
  return withKeepTones(profile, contextualFillSwapSplit(profile, plateHex).allowed);
}

/**
 * A FREE-FLOATING PIECE OF THE MARK THAT THIS PLATE SWALLOWS WHOLE — Ryan's carrolllynx card
 * (2026-09-22), and his elite-edge one, which turn out to be a single complaint.
 *
 *   carrolllynx cfg3  *"The white text portion of the logo is just white on white in this
 *     version… this logo is an anomaly in that it has 2 free-floating sections to it. One section
 *     is COMPLETELY white, and another section is multiple colors INCLUDING white… If we change
 *     the white everywhere, well that's hard to do b/c it'll likely break the multi-colored part
 *     of the logo. If we teach the tool to ONLY change the free-floating white-only portion of the
 *     logo, then that could be a potential fix."*
 *   elite-edge cfg2   *"the logo features lots of intricate white letters on the plate, and these
 *     all get lost on the white plate."*
 *
 * Nothing in the app looked at COMPONENTS — only at tones and at bands — so on both plates
 * neither recolour door is reached and no ring is added (measured at HEAD: `contextualFillSwap`
 * [], `refusedRecolours` [], `logoAddOutline` null). The mark reads; one of its PARTS is gone.
 *
 * ⛔ FOUR CONDITIONS, AND THREE OF THEM ARE THERE BECAUSE A LOOSER VERSION WAS MEASURED AND
 * REVERSED PLATES HE HAS ALREADY ANSWERED. In order, with what each one costs if dropped:
 *   1. a single-tone free-floating piece is swallowed by the plate  — his condition
 *   2. the rest of the mark still READS here. Without it this fires where the whole mark ghosts,
 *      which `plateDealDeck` already refuses outright.
 *   3. NOTHING THE APP ALREADY HAS RESCUES THAT TONE HERE — no swap proposed, none refused, no
 *      band repainted or kept, no ring on the logo. This is the one that keeps HIS NINE safe:
 *      without it the rule reaches layton cfg1, wallabies cfg1 and gochatham cfg1, all three
 *      approved, where the existing ladder already recolours or deliberately keeps.
 *   4. the swallowed piece is a MINORITY of the mark. Past half, the mark's COLOUR is the plate's
 *      colour and that is the recolour ladder's business, not a per-piece repair —
 *      frostburgsports' nine pieces are all one tone and all ghost together (100%), and
 *      athletics-hanover cfg1 is 79%, a NAMED CONTROL.
 *
 * ⛔ AND THE NARROWER READINGS WERE TRIED FIRST AND BOTH FAILED. "Refuse to deal the plate" (his
 * own second suggestion) reaches 28 plates including three of his nine, and no share bar separates
 * them — gochatham sits at 17.4%, above carrolllynx at 15.9%. "Another whole PIECE must survive",
 * which is his Carroll sentence read literally, EXCLUDES elite-edge cfg2, because that mark's navy
 * body is a 64%-dominant multi-tone component that never clears the solid bar.
 *
 * ⚠ THE TARGET IS A COLOUR THE MARK ALREADY WEARS, the same rule as the added ring's
 * (`syncLogoContrastRing`, 2026-09-22): `markToneOrder` heaviest-first, first one that reads here
 * and is not the tone being replaced. If the mark has nothing legible to offer, there is no swap —
 * inventing a colour for one component is not a repair.
 *
 * @returns {Array<{from:string, to:string, pieceOnly:true}>} at most one entry per ghosted tone
 */
export function ghostedPieceSwaps(profile, plateHex, opts = {}) {
  const p = profile || {};
  const plate = normalizeHex(plateHex);
  if (!plate) return [];
  if (opts.ringed) return [];                                    // condition 3, the ring half
  const pieces = (Array.isArray(p.logoInkPieces) && p.logoInkPieces)
    || (Array.isArray((p.logoStructure || {}).inkPieces) && p.logoStructure.inkPieces) || [];
  const solid = pieces.filter((x) => x && x.hex && (x.dom || 0) >= INK_PIECE_SOLID_SHARE);
  if (!solid.length) return [];
  const ls = p.logoStructure || {};
  const tones = ((Array.isArray(ls.traceTones) && ls.traceTones) || p.logoTraceTones || [])
    .filter((t) => t && t.hex && (t.share || 0) >= 0.03);
  if (tones.length < 2) return [];
  // 2 — the rest of the mark still reads here
  if (!tones.some((t) => readsOn(plate, normalizeHex(t.hex)))) return [];
  // 3 — nothing the existing ladder does on this plate
  if ((contextualFillSwap(p, plate) || []).length) return [];
  if ((refusedRecolours(p, plate) || []).length) return [];
  const bands = (Array.isArray(ls.bands) ? ls.bands : []).map(normalizeHex).filter(Boolean);
  if (bands.length) {
    const view = bandConflictView(bands, plate, bandViewOptions(p));
    if (Object.keys(view.repaint || {}).length || (view.kept || []).length) return [];
  }
  const order = markToneOrder(p, plate).map(normalizeHex).filter(Boolean);
  const out = [];
  const done = new Set();
  for (const piece of solid) {
    const from = normalizeHex(piece.hex);
    if (!from || done.has(from) || !colorsConflict(from, plate)) continue;   // 1
    // 4 — a minority of the mark
    const share = solid.filter((x) => colorsIndistinguishable(normalizeHex(x.hex), from))
      .reduce((n, x) => n + (x.share || 0), 0);
    if (share >= 0.5) continue;
    const to = order.find((h) => !colorsConflict(h, from) && readsOn(plate, h));
    if (!to) continue;
    done.add(from);
    out.push({ from, to, pieceOnly: true });
  }
  return out;
}

/** Every recolour BOTH doors wanted to make on this plate and the gate refused - the set that is
 *  now relying on a contrast ring instead. Empty on every plate the rule does not touch. */
export function refusedRecolours(profile, plateHex) {
  const p = profile || {};
  const out = contextualFillSwapSplit(p, plateHex).refused.map((sw) => ({ door: 'swap', ...sw }));
  const ls = p.logoStructure || {};
  const bands = Array.isArray(ls.bands) ? ls.bands.map(normalizeHex).filter(Boolean) : [];
  if (bands.length) {
    const view = bandConflictView(bands, normalizeHex(plateHex), bandViewOptions(p));
    for (const i of (view.kept || [])) out.push({ door: 'band', from: bands[i], to: null });
  }
  return out;
}

// ── pure raster peel (testable in node against synthetic ImageData) ───────────

const KEY_TOL2 = 66 * 66; // Euclidean RGB radius² for "this pixel is that band"

/**
 * Peel ONE band colour off an RGBA buffer by a border-connected flood: starting from
 * the canvas edge, walk through already-transparent pixels AND pixels within tolerance
 * of `keyRgb`, knocking the matching ones to alpha 0; stop at any opaque pixel of a
 * DIFFERENT colour (the next band / the fill). Interior instances of the colour that
 * aren't edge-connected are preserved. Mutates `data` in place. Returns removed count.
 */
export function borderFloodPeel(data, w, h, keyRgb) {
  const n = w * h;
  const t2 = KEY_TOL2;
  const passable = (p) => {
    const i = p * 4;
    if (data[i + 3] < 8) return { enter: true, knock: false }; // transparent → flood passes through
    const dr = data[i] - keyRgb[0], dg = data[i + 1] - keyRgb[1], db = data[i + 2] - keyRgb[2];
    if (dr * dr + dg * dg + db * db <= t2) return { enter: true, knock: true }; // band pixel → remove
    return { enter: false, knock: false }; // a different opaque colour → wall
  };
  const visited = new Uint8Array(n);
  const stack = [];
  const seed = (x, y) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const p = y * w + x;
    if (visited[p]) return;
    visited[p] = 1;
    const r = passable(p);
    if (r.enter) stack.push(p * 2 + (r.knock ? 1 : 0));
  };
  for (let x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
  for (let y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }
  let removed = 0;
  while (stack.length) {
    const enc = stack.pop();
    const p = enc >> 1;
    if (enc & 1) { data[p * 4 + 3] = 0; removed += 1; } // knock out the band pixel's alpha
    const x = p % w;
    const y = (p / w) | 0;
    seed(x - 1, y); seed(x + 1, y); seed(x, y - 1); seed(x, y + 1);
  }
  return removed;
}

/** Peel `count` outer bands off an ImageData in place (outside-in). Pure. */
export function peelBandsFromImageData(imageData, bandsOuterToInner, count) {
  const { data, width: w, height: h } = imageData;
  const k = Math.max(0, Math.min(count | 0, bandsOuterToInner.length));
  for (let i = 0; i < k; i++) borderFloodPeel(data, w, h, hexToRgb(bandsOuterToInner[i]));
  return imageData;
}

// Cap for the edge-blend rescue in `labelBandsFromBorder`. The defringe (bg-detect, pass 6)
// keeps only blends NEARER the ink than the key, so a surviving band-side blend sits under half
// the band↔key distance — Hanover's white-on-navy pair puts that ceiling at ~159 (measured
// survivors 107–138). The fill-side tie-break is what carries the real safety; this cap just
// refuses far colours when the fill is unknown.
const EDGE_BLEND_TOL2 = 160 * 160;

/**
 * The PER-PIXEL half of `labelBandsFromBorder` — "is this pixel that band's colour?" — with no
 * flood around it. Factored out (WD 442) so the REPAINT can ask the flood's own question about a
 * pixel the flood never reaches, without a second copy of the tolerance rules; `labelBandsFromBorder`
 * is unchanged, it just calls this for every pixel it visits.
 * @returns {(p:number)=>number} band index · -1 opaque non-band (the fill / interior) · -2 transparent.
 */
function bandMatcherFor(imageData, bandsOuterToInner, fillHex) {
  const { data, width: w, height: h } = imageData;
  const N = bandsOuterToInner ? bandsOuterToInner.length : 0;
  const rgb = N ? bandsOuterToInner.map(hexToRgb) : [];
  const fill = normalizeHex(fillHex) ? hexToRgb(fillHex) : null;
  const t2 = KEY_TOL2;
  const dist2 = (i, c) => {
    const dr = data[i] - c[0], dg = data[i + 1] - c[1], db = data[i + 2] - c[2];
    return dr * dr + dg * dg + db * db;
  };
  const touchesAlpha = (p) => {
    const x = p % w, y = (p / w) | 0;
    return (x > 0 && data[(p - 1) * 4 + 3] < 8) || (x < w - 1 && data[(p + 1) * 4 + 3] < 8)
      || (y > 0 && data[(p - w) * 4 + 3] < 8) || (y < h - 1 && data[(p + w) * 4 + 3] < 8);
  };
  return (p) => {
    const i = p * 4;
    if (data[i + 3] < 8) return -2; // transparent → flood passes through, not a band
    let best = -1, bd = t2;
    for (let bi = 0; bi < N; bi++) {
      const d = dist2(i, rgb[bi]);
      if (d <= bd) { bd = d; best = bi; }
    }
    if (best >= 0) return best;
    // Edge-blend rescue (see `labelBandsFromBorder` ⛔): only ON a transparency edge, only band-leaning.
    let eb = -1, ed = EDGE_BLEND_TOL2;
    for (let bi = 0; bi < N; bi++) {
      const d = dist2(i, rgb[bi]);
      if (d <= ed) { ed = d; eb = bi; }
    }
    if (eb >= 0 && (!fill || ed < dist2(i, fill)) && touchesAlpha(p)) return eb;
    return -1; // opaque but not a band (the fill / interior) → a wall
  };
}

/**
 * Every opaque pixel that WEARS a band's colour, connectivity ignored — the same per-pixel test
 * `labelBandsFromBorder` floods with, asked of the whole raster.
 *
 * ⛔ THIS IS NOT A REPLACEMENT FOR THE FLOOD AND MUST NEVER BE USED FOR A PEEL. The flood's
 * border-connectedness is the load-bearing half of `borderFloodPeel`: an interior instance of a
 * band colour knocked to alpha punches a hole through the artwork. It is the right question for a
 * REPAINT, which removes nothing — see `remapBandsFromImageData`'s note on the half-repainted band.
 * @returns {Int16Array} per-pixel band index, -1 for "not a band" (fill / interior / background).
 */
export function labelBandTones(imageData, bandsOuterToInner, fillHex) {
  const N = bandsOuterToInner ? bandsOuterToInner.length : 0;
  const n = imageData.width * imageData.height;
  const tone = new Int16Array(n).fill(-1);
  if (N <= 0) return tone;
  const nearestBand = bandMatcherFor(imageData, bandsOuterToInner, fillHex);
  for (let p = 0; p < n; p++) { const b = nearestBand(p); if (b >= 0) tone[p] = b; }
  return tone;
}

/**
 * The border-connected flood that decides which pixels ARE each band — factored out of
 * `remapBandsFromImageData` so the question "can this band be acted on at all?" is answered by the
 * very code that would act on it (Ryan 2026-08-15, the EE Logo).
 *
 * ⛔ THE DETECTOR AND THE PEEL DISAGREE ABOUT WHAT "OUTER" MEANS, AND THE UI BELIEVED THE WRONG ONE.
 * `logo-structure.analyzeStructure` orders layers by DEPTH, so a shell that sits outermost in the
 * depth ordering is a band. This flood starts at the image border and walks INWARD, treating any
 * opaque non-band pixel as a wall — so a shell the artwork encloses is unreachable however outer it
 * looks by depth. On the Elite Edge lockup the key leaves the charcoal disc standing between the
 * border and the white ring, so the detector reports a white band and this flood labels not one
 * pixel of it: the buyer gets an outline chip whose ✕ is measurably a no-op.
 *   Returning the label array rather than duplicating the walk is the whole point — a second
 * implementation of this rule is the trap CLAUDE.md §0b names, and here the two implementations
 * ALREADY existed as two different definitions of "outer". This is now the only one that decides.
 *
 * ⛔ AND "BORDER-CONNECTED" WAS THE WRONG HALF OF THAT RULE (Ryan 2026-08-18, his tenth Hanover
 * finding: on the red plate the counter rims came out "blue" while the exterior wore the white
 * keyline). The flood walked in from the canvas border, so the keyline fragments around the
 * see-through-CUT counters — walled behind the letter fill — were never labelled, and every
 * treatment that acts on the label split one ring into mismatched halves: the flatten painted
 * them with the pop ink, a repaint recoloured only the exterior, a peel left them standing.
 * The question the label answers is "does the PLATE show against this band pixel?", and the
 * plate shows through EVERY transparent region, not just the border-connected one — so the
 * flood now also seeds from every transparent pixel. What must NOT change doesn't: opaque
 * non-band ink is still the wall, so the Elite Edge shell (enclosed entirely by opaque ink,
 * no plate beside it anywhere) still labels not one pixel.
 *
 * ⛔ AND SEEDING ALONE WAS NOT ENOUGH — measured on the real keyed raster, the interior rims
 * are WALLED IN BLEND (probe-rim-label.mjs): a ~1px anti-aliased edge toward the keyed-out
 * field (~#92A4B6, RGB-distance ~107 from the white band) wraps each rim's core on BOTH sides,
 * outside KEY_TOL's 66, so the flood could touch the moat and never enter the ring. The
 * exterior only ever labelled because one crack somewhere on its long perimeter fell inside
 * tolerance. The rescue is pass 6's own defringe rule applied at the label — *a blend belongs
 * to whichever side it is closer to*: a pixel ON a transparency edge that misses the strict
 * tolerance still labels as a band when it is band-LEANING — within EDGE_BLEND_TOL2 of that
 * band AND strictly closer to it than to `fillHex`. Fill-side blends fail both tests (the
 * fill's own edge stays the wall), and pixels not touching transparency are never widened,
 * so interior artwork keeps the strict test.
 *
 * @param {string|null} [fillHex] the mark's fill, for the band-leaning tie-break (optional —
 *   without it the edge rescue relies on EDGE_BLEND_TOL2 alone).
 * @returns {Int16Array} per-pixel band index, -1 for "not a band" (fill / interior / background).
 */
export function labelBandsFromBorder(imageData, bandsOuterToInner, fillHex) {
  const { data, width: w, height: h } = imageData;
  const N = bandsOuterToInner ? bandsOuterToInner.length : 0;
  const n = w * h;
  const label = new Int16Array(n).fill(-1);
  if (N <= 0) return label;
  const nearestBand = bandMatcherFor(imageData, bandsOuterToInner, fillHex);
  const visited = new Uint8Array(n);
  const stack = [];
  const seed = (x, y) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const p = y * w + x;
    if (visited[p]) return;
    visited[p] = 1;
    const b = nearestBand(p);
    if (b === -2) { stack.push(p); return; } // transparent → keep flooding
    if (b >= 0) { label[p] = b; stack.push(p); } // a band → label + keep flooding inward
    // b === -1 (fill/interior) → a wall; don't push
  };
  for (let x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
  for (let y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }
  // Every transparent region is a flood source — the plate shows through an interior cutout
  // (a see-through counter) exactly as it shows past the silhouette, so the band fragments
  // rimming it must be labelled with the rest of their ring (see the header's tenth-finding ⛔).
  for (let p = 0; p < n; p++) {
    if (!visited[p] && data[p * 4 + 3] < 8) { visited[p] = 1; stack.push(p); }
  }
  while (stack.length) {
    const p = stack.pop();
    const x = p % w, y = (p / w) | 0;
    seed(x - 1, y); seed(x + 1, y); seed(x, y - 1); seed(x, y + 1);
  }
  return label;
}

/**
 * How many pixels each band actually owns, by the peel's own reckoning. A band with none of them is
 * a control that cannot do anything — see `labelBandsFromBorder`.
 * @returns {number[]} one count per band, outer→in.
 */
export function bandPixelCounts(imageData, bandsOuterToInner, fillHex) {
  const N = bandsOuterToInner ? bandsOuterToInner.length : 0;
  const counts = new Array(N).fill(0);
  if (!N || !imageData) return counts;
  const label = labelBandsFromBorder(imageData, bandsOuterToInner, fillHex);
  for (let p = 0; p < label.length; p++) { const b = label[p]; if (b >= 0) counts[b] += 1; }
  return counts;
}

/**
 * REMAP the logo's outline bands + optionally flatten its CENTER to one colour. One
 * border-connected flood from the edge labels every outline pixel to its nearest band
 * colour; then:
 *   • DISABLED bands are "replaced" like the vector text collapses — a disabled band takes
 *     the colour of its nearest ENABLED band OUTWARD (the outer outline replaces a disabled
 *     inner one); an outermost-disabled band peels to transparent (Ryan 2026-07-21).
 *   • when `centerRgb` is set, EVERY opaque pixel that ISN'T an outline band (the fill + any
 *     interior detail — the "purple with bits of black/green") is recoloured to it, so the
 *     whole logo interior becomes one tone; the cutouts (transparent) are left alone
 *     (Ryan 2026-07-22).
 *   • when `bandColors` gives an override [r,g,b] for a band index, that ENABLED band is
 *     REPAINTED to it (the buyer recoloured that outline in the Colors panel). A disabled
 *     inner band collapsing outward adopts its neighbour's OVERRIDE colour too, so the
 *     collapse stays consistent (Ryan 2026-07-22).
 * Pure (testable in node). `disabled` = a Set of band indices (outer→in); `centerRgb` =
 * [r,g,b] or null; `bandColors` = {index:[r,g,b]} or null. Interior instances of a band
 * colour aren't edge-connected, so they read as CENTER (recoloured), not as a band.
 */
export function remapBandsFromImageData(imageData, bandsOuterToInner, disabled, centerRgb, bandColors, fillRemap, fillHex) {
  const { data, width: w, height: h } = imageData;
  const N = bandsOuterToInner ? bandsOuterToInner.length : 0;
  const hasDisabled = !!(disabled && disabled.size);
  const overrides = bandColors && typeof bandColors === 'object' ? bandColors : null;
  const hasOverride = !!(overrides && Object.keys(overrides).length);
  // Contextual-contrast fill swaps (Ryan 2026-07-23): [{from:[r,g,b], to:[r,g,b]}] — repaint a
  // NON-band interior color that merges with the dyed plate to a standout brand color. Applied
  // only when the interior isn't being flattened by centerRgb (a manual flatten wins).
  const swaps = !centerRgb && Array.isArray(fillRemap) ? fillRemap.filter((s) => s && s.from && s.to) : [];
  const hasSwaps = swaps.length > 0;
  /* ⭐ A PIECE-SCOPED SWAP PAINTS ONE COMPONENT, NOT ONE COLOUR (WD 455, Ryan's carrolllynx card —
   * see `ghostedPieceSwaps`). Its mask is flooded from THIS ImageData, the one about to be
   * repainted, rather than from the census raster: the two are different sizes and a mask built at
   * the wrong scale paints the wrong pixels. `pieceMaskForTone` is `measureInkPieces`' own flood.
   * ⚠ Built once per swap here, not per pixel. */
  /* ⛔ AND THE PAINTER TAKES EVERY PIECE, NOT ONLY THE ONES THE MEASUREMENT KEPT (WD 459, Ryan
   * re-raising carrolllynx cfg3 in pass 8). `measureInkPieces` drops a component under 5% of the
   * mark, which is right for DECIDING (a speck is not a piece) and wrong for PAINTING: CARROLL's
   * A, R, R sit at 5.3% and were repainted, C, O, L, L sit just under and stayed white on the cream
   * plate — half a word moved. The rule still decides on the measured pieces; once it has, every
   * free-floating solid piece of that tone is the same "white-only portion" he asked to move. */
  for (const sw of swaps) {
    if (!sw.pieceOnly || sw.mask !== undefined) continue;
    sw.mask = (Array.isArray(sw.tones) && sw.tones.length > 1 && sw.fromHex)
      ? pieceMaskForTone(data, w, h, sw.tones, sw.fromHex, INK_PIECE_SOLID_SHARE, 0) : null;
  }
  const pieceSwaps = swaps.filter((s) => s.pieceOnly && s.mask);
  /* ⭐ WD 474 - LETTERING INSIDE A FLIPPED PIECE FLIPS THE OTHER WAY. Carroll's LYNX banner is dark with white
     letters; on the black plate the banner swaps dark -> white and the white letters vanished into it (Ryan:
     *"it's white on white and you can no longer read LYNX"*). Pixels of the swap's TARGET colour lying INSIDE
     the piece (two pixels or more from its outer edge, so a letter's own anti-aliased rim is never touched)
     take the swap's SOURCE colour: the two tones exchange within the piece, and the lettering reads. */
  for (const sw of pieceSwaps) {
    const inner = new Uint8Array(w * h);
    for (let q = 0; q < w * h; q++) {
      if (!sw.mask[q]) continue;
      const x = q % w, y = (q / w) | 0;
      let ok = true;
      for (let dy = -2; dy <= 2 && ok; dy++) for (let dx = -2; dx <= 2; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h || !sw.mask[ny * w + nx]) { ok = false; break; }
      }
      if (ok) inner[q] = 1;
    }
    sw.inner = inner;
  }
  /** A piece-scoped swap owns its component's pixels OUTRIGHT — ahead of the band labeller. */
  const paintPiece = (p, i) => {
    for (const sw of pieceSwaps) {
      if (!sw.mask[p]) continue;
      const f = sw.from;
      const dr = data[i] - f[0], dg = data[i + 1] - f[1], db = data[i + 2] - f[2];
      if (dr * dr + dg * dg + db * db <= KEY_TOL2) {
        data[i] = sw.to[0]; data[i + 1] = sw.to[1]; data[i + 2] = sw.to[2];
        return true;
      }
      if (sw.inner && sw.inner[p]) {
        const t = sw.to, er = data[i] - t[0], eg = data[i + 1] - t[1], eb = data[i + 2] - t[2];
        if (er * er + eg * eg + eb * eb <= KEY_TOL2) {
          data[i] = f[0]; data[i + 1] = f[1]; data[i + 2] = f[2];
          return true;
        }
      }
    }
    return false;
  };
  if (!hasDisabled && !centerRgb && !hasOverride && !hasSwaps) return imageData;
  const n = w * h;
  const rgb = N ? bandsOuterToInner.map(hexToRgb) : [];
  // The colour each band PAINTS at: its override if the buyer set one, else its own.
  const paint = rgb.map((c, i) => (overrides && overrides[i]) ? overrides[i] : c);
  // Effective colour per band: its paint colour if enabled; else the nearest ENABLED band
  // outward (smaller index)'s paint colour; null (→ transparent) if every band outward is
  // also disabled.
  const eff = rgb.map((c, i) => {
    if (!disabled || !disabled.has(i)) return paint[i];
    let j = i - 1;
    while (j >= 0 && disabled.has(j)) j--;
    return j >= 0 ? paint[j] : null;
  });
  // -1 = not an outline band (fill / interior / bg). ⚠ ONE implementation, shared with
  // `bandPixelCounts` — see `labelBandsFromBorder` for why that matters here specifically.
  /* ⛔ AN ENCLOSED INSTANCE OF A BAND'S COLOUR IS NOT REPAINTED, AND THAT IS RYAN'S ANSWER, NOT AN
   * OVERSIGHT (2026-09-19, WD 442 — he answered a five-plate page). It was built the other way and
   * reverted: `labelBandTones` (kept, and what the probes read) labels band ink the flood cannot
   * reach, and feeding it to the repaint made cherry-valley's runner white in her entirety, which
   * is what he had asked for in writing.
   *   ⭐ IT ALSO MOVED THREE PLATES HE HAD APPROVED — springboro, rockfordregents, gotuftsjumbos —
   * and on each one the mark's interior detail (mane strokes, a crown, an elephant's wrinkle lines)
   * followed the outer edge to black and the modelling inside the mark flattened. He chose the
   * CURRENT behaviour on all three, and then on his own flagged plate too, conditionally:
   *     *"right because to the naked eye it looks like the running figure is one unbroken
   *      continuous shape -- but the closer I look and squint, that MAY not be the case, in which
   *      case, the left is acceptable."*
   *   ⛔ MEASURED, AND HIS SQUINT WAS RIGHT: the runner is **eleven separate black pieces** fenced
   * apart by pink hairlines (`app/scratchpad/runner-split.mjs`). She is not one shape, so the
   * premise for the whole-tone repaint does not hold on the only plate that wanted it.
   *   ⛔ DO NOT REBUILD IT ON "the same tone should not be split" — that is the reasoning this
   * reverted, and four axes were measured before it (connectivity, which is vacuous by
   * construction; distance; contrast against the encloser; ink share). See
   * NEXT-SESSION-CORPUS-AUDIT.md, WD 442. */
  const label = labelBandsFromBorder(imageData, bandsOuterToInner, fillHex);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    const b = label[p];
    /* ⛔ A FREE-FLOATING PIECE IS NOT A BAND, EVEN WHEN IT WEARS THE BAND'S COLOUR (WD 459,
     * mountmercy cfg4: *"the black free-floating text never changed colors"*). The border flood
     * labels any ink touching the background in the outer band's tone as that band, so the black
     * lettering was filed under band 0 and the swap branch below never saw it. `ghostedPieceSwaps`
     * already refuses whenever the band view wants to act, so this cannot fight a band decision.
     * Not under a manual flatten: that is the buyer's own "one tone", and it wins. */
    if (!centerRgb && pieceSwaps.length && data[i + 3] >= 8 && paintPiece(p, i)) continue;
    if (b >= 0) {
      if (hasDisabled && disabled.has(b)) {
        const e = eff[b];
        if (e === null) data[i + 3] = 0; // peel
        else { data[i] = e[0]; data[i + 1] = e[1]; data[i + 2] = e[2]; } // recolor to outer neighbour
      } else if (overrides && overrides[b]) {
        const e = overrides[b]; // enabled band the buyer recoloured → repaint to the override
        data[i] = e[0]; data[i + 1] = e[1]; data[i + 2] = e[2];
      }
    } else if (centerRgb && data[i + 3] >= 8) {
      // Not a band + opaque → the logo CENTER (fill + interior detail); flatten to one tone.
      // ⛔ AHEAD OF THE ENCLOSED REPAINT BELOW, and deliberately: a manual flatten is "make the
      // whole interior one tone", and exempting the interior instances of a band colour from it
      // would leave the buyer's flatten with holes in it.
      data[i] = centerRgb[0]; data[i + 1] = centerRgb[1]; data[i + 2] = centerRgb[2];
    } else if (hasSwaps && data[i + 3] >= 8) {
      // Not a band + opaque + no flatten → a fill region; if it matches a conflicting brand
      // color (within tolerance), repaint it to that color's standout target (green→yellow).
      for (let s = 0; s < swaps.length; s++) {
        // a piece-scoped swap only owns the pixels of its own component (WD 455)
        if (swaps[s].pieceOnly && !(swaps[s].mask && swaps[s].mask[p])) continue;
        const f = swaps[s].from;
        const dr = data[i] - f[0], dg = data[i + 1] - f[1], db = data[i + 2] - f[2];
        const df = dr * dr + dg * dg + db * db;
        // WD 475: a pixel nearer to a different ink inside the radius is that ink's (`withKeepTones`)
        if (df <= KEY_TOL2 && swaps[s].keep && swaps[s].keep.some((k) => {
          const kr = data[i] - k[0], kg = data[i + 1] - k[1], kb = data[i + 2] - k[2];
          return kr * kr + kg * kg + kb * kb < df;
        })) continue;
        if (df <= KEY_TOL2) {
          const t = swaps[s].to;
          data[i] = t[0]; data[i + 1] = t[1]; data[i + 2] = t[2];
          break;
        }
      }
    }
  }
  return imageData;
}

// ── ADD an outer outline (C) — dilate the alpha silhouette + fill a ring ───────
// The opposite of peeling: for a logo whose own outer edge is LOST on a matching plate
// (a black keyline on a black plate → looks smaller), draw a fresh ring of a chosen
// colour (usually white) around the silhouette so it reads full-size again. A distance
// transform from the mark gives a clean, solid, anti-aliased ring; the canvas is PADDED
// so the ring isn't clipped on an auto-trimmed logo. Reversible (a transient raster).

const MASK_ALPHA = 128; // ≥ this = solid mark (the ring starts at the 50%-alpha contour)

/* ── DUST IS NOT SILHOUETTE (Ryan 2026-08-07, Broncos 1.jpg) ──────────────────────────────────────
 * *"instead of it forming a perfect enclosed circular/ring outline around the main circle, it has
 * gaps in the ring at the top, bottom, left, and right."*
 *
 * Measured, and the gaps are the only part that was RIGHT. That badge's keyed raster carries 226
 * connected components: the badge (99.756% of the ink) and **225 specks of 6-to-11 pixels** — a
 * faint, broken grey ring the source JPEG has ~6px outside the artwork, light enough (mean RGB
 * 228,224,226) to be invisible on the plate and just dark enough to survive the background key.
 * A distance transform trusts every seeded pixel equally, so the ring was being dilated around the
 * DUST and ballooned ~9px past the badge wherever dust survived.
 *
 * ⚠ AND THE FOUR-FOLD SYMMETRY IS THE TELL, not a coincidence. The upload trim crops to an ink
 * BOUNDING BOX, so it can only discard residue from four straight edges: the dust directly above,
 * below, left and right of a disc that fills its box has nowhere to be and is cropped, while the
 * dust at every diagonal sits in the corners and survives. Two passes disagreeing about the same
 * residue — one removing it in the only shape a rectangle can express, the other faithfully
 * outlining what was left — is what put four gaps at exactly 12/3/6/9 o'clock.
 *
 * So the seed is filtered by CONNECTED COMPONENT, which is the shape the question actually has.
 * A component below `SPECK_SHARE` of the largest one is not artwork and gets no ring.
 *
 * ⛔ THE SEED ONLY — the composite still DRAWS every pixel it was given. Deciding a speck is not
 * silhouette is this function's business; deleting the buyer's ink is not, and doing it as a side
 * effect of adding an outline would mean the artwork changed depending on whether an outline
 * happened to be on.
 *
 * ⛔ AND THE DUST ITSELF STAYS — RYAN'S CALL, 2026-08-07, DO NOT "FINISH" THIS. With the ring fixed
 * he could still see the residue as a broken second arc outside it, so the obvious next move is to
 * despeckle in the KEYER and be rid of it everywhere. He looked at his source file, found the dust
 * was already in the JPEG, and stopped it: *"not like we created this problem… not the end of the
 * world if we can't fix it, b/c I understand if we fix it fully, we risk breaking other kinds of
 * logos which don't need fixing (or breaking)."*
 *   He is right about the risk, and it is worth stating precisely so nobody re-derives it the
 * expensive way: a component filter in the keyer DELETES ink permanently, and the same test that
 * catches a 6px speck also catches a detached ® or the dot of an `i` on a mark whose main shape is
 * a thousand times their size. Here the worst case is a small piece that goes un-outlined and still
 * renders. That asymmetry is the whole reason this lives in the seed. If it is ever revisited, it
 * needs `tools/probe-logo-key.mjs` over the full corpus first, measuring ink loss per logo — not a
 * threshold that looked right on one badge.
 */
const SPECK_SHARE = 0.001;

/**
 * The chamfer seed: `alpha >= MASK_ALPHA`, minus the dust. Returns a `w*h` 0/1 mask.
 *
 * 8-connected, because residue speckle touches diagonally as often as not — labelling it
 * 4-connected would split one speck into three and change nothing about the verdict, but it would
 * make the size test read smaller than the thing it is measuring.
 *
 * Iterative, never recursive: this runs on rasters up to ~1024² and a flood that deep would blow
 * the stack (the rule `bg-detect.js`'s own flood states, for the same reason).
 */
function buildSeedMask(sd, w, h) {
  const n = w * h;
  const solid = new Uint8Array(n);
  for (let p = 0; p < n; p++) if (sd[p * 4 + 3] >= MASK_ALPHA) solid[p] = 1;
  const label = new Int32Array(n).fill(-1);
  const sizes = [];
  const stack = new Int32Array(n);
  for (let s = 0; s < n; s++) {
    if (!solid[s] || label[s] >= 0) continue;
    const id = sizes.length;
    let top = 0, count = 0;
    label[s] = id; stack[top++] = s;
    while (top > 0) {
      const p = stack[--top];
      count++;
      const x = p % w, y = (p / w) | 0;
      for (let dy = -1; dy <= 1; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= w) continue;
          const q = ny * w + nx;
          if (solid[q] && label[q] < 0) { label[q] = id; stack[top++] = q; }
        }
      }
    }
    sizes.push(count);
  }
  if (sizes.length < 2) return solid; // one shape, or none — nothing to judge the others against
  let largest = 0;
  for (let i = 0; i < sizes.length; i++) if (sizes[i] > largest) largest = sizes[i];
  const floor = largest * SPECK_SHARE;
  // Below 1px the test cannot discard anything, and on a mark small enough for that to happen every
  // component IS large. Bail rather than run a pass that can only be a no-op.
  if (!(floor >= 1)) return solid;
  for (let p = 0; p < n; p++) if (solid[p] && sizes[label[p]] < floor) solid[p] = 0;
  return solid;
}

/**
 * Build a NEW ImageData: `src` with a `thickness`-px outer ring in `colorRgb`, on a
 * canvas padded by `thickness` on every side. Pure (testable in node). The original
 * mark composites OVER the ring, so its own anti-aliased edge stays clean.
 */
export function buildOutlinedImageData(src, thickness, colorRgb) {
  const N = Math.max(1, thickness | 0);
  const w = src.width, h = src.height, sd = src.data;
  const pw = w + 2 * N, ph = h + 2 * N, pn = pw * ph;
  const seed = buildSeedMask(sd, w, h); // alpha contour, minus dust — see above
  // Mask on the padded grid (src sits at +N,+N). 0 = mark, INF = outside → chamfer seed.
  const INF = 1 << 28;
  const dist = new Int32Array(pn);
  for (let y = 0; y < ph; y++) {
    for (let x = 0; x < pw; x++) {
      const sx = x - N, sy = y - N;
      let solid = false;
      if (sx >= 0 && sy >= 0 && sx < w && sy < h) solid = seed[sy * w + sx] === 1;
      dist[y * pw + x] = solid ? 0 : INF;
    }
  }
  chamferInPlace(dist, pw, ph); // → distance from the mark, in /3 units
  const out = new Uint8ClampedArray(pn * 4);
  const [cr, cg, cb] = colorRgb;
  const nUnits = N * 3; // compare in chamfer units
  for (let y = 0; y < ph; y++) {
    for (let x = 0; x < pw; x++) {
      const p = y * pw + x;
      const d = dist[p]; // 0 at mark, grows outward
      // Ring alpha: solid within N, 1px anti-aliased falloff at the outer edge.
      let ringA = 0;
      if (d > 0) {
        if (d <= nUnits + 3) ringA = clamp((nUnits + 3 - d) / 3, 0, 1);
      } else {
        // d==0 (a mark pixel): give BOUNDARY pixels a 1px inward OVERLAP so the ring tucks
        // under the mark's edge and hides rough cutting (Ryan 2026-07-21). Interior mark
        // pixels get none. The mark composites on top, so this only SHOWS where the edge
        // is rough / semi-transparent (an intentional clean edge stays clean).
        const up = y > 0 ? dist[p - pw] : 1, dn = y < ph - 1 ? dist[p + pw] : 1;
        const lf = x > 0 ? dist[p - 1] : 1, rt = x < pw - 1 ? dist[p + 1] : 1;
        if (up > 0 || dn > 0 || lf > 0 || rt > 0) ringA = 1;
      }
      // Source pixel (mark) at this location.
      const sx = x - N, sy = y - N;
      let sr = 0, sg = 0, sb = 0, sa = 0;
      if (sx >= 0 && sy >= 0 && sx < w && sy < h) {
        const si = (sy * w + sx) * 4;
        sr = sd[si]; sg = sd[si + 1]; sb = sd[si + 2]; sa = sd[si + 3] / 255;
      }
      // Composite src OVER a ring-coloured layer.
      const rA = ringA * (1 - sa);
      const oa = sa + rA;
      const oi = p * 4;
      if (oa <= 0) { out[oi] = out[oi + 1] = out[oi + 2] = out[oi + 3] = 0; continue; }
      out[oi] = (sr * sa + cr * rA) / oa;
      out[oi + 1] = (sg * sa + cg * rA) / oa;
      out[oi + 2] = (sb * sa + cb * rA) / oa;
      out[oi + 3] = Math.round(oa * 255);
    }
  }
  return { data: out, width: pw, height: ph };
}

/** Chamfer (3-4) distance transform IN PLACE (seed: 0 = source, INF = elsewhere). */
function chamferInPlace(d, w, h) {
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? (1 << 28) : d[y * w + x]);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const p = y * w + x; if (d[p] === 0) continue;
    let m = d[p];
    const a = at(x - 1, y) + 3, b = at(x, y - 1) + 3, c = at(x - 1, y - 1) + 4, e = at(x + 1, y - 1) + 4;
    if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e; d[p] = m;
  }
  for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
    const p = y * w + x; if (d[p] === 0) continue;
    let m = d[p];
    const a = at(x + 1, y) + 3, b = at(x, y + 1) + 3, c = at(x + 1, y + 1) + 4, e = at(x - 1, y + 1) + 4;
    if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e; d[p] = m;
  }
}
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/**
 * Add an outer outline to a logo dataUrl. `thicknessFrac` = ring width as a fraction of the logo's
 * LONG edge. Returns { url, aspect } (aspect changes because the canvas grew). No colour /
 * 0 thickness → { url: dataUrl, aspect: null }.
 *
 * ⛔ IT WAS THE SHORT EDGE, AND THAT MADE THE RING LIE ABOUT ITS OWN WEIGHT (Ryan 2026-09-18,
 * bobcats-phsc-edu cfg1/cfg3: *"the tool reports a black outermost outline here and I can't see one
 * in the render on either the logo or the number… worth checking whether the 'add outline' step is
 * actually recolouring part of the logo instead of adding a stroke."* It is a stroke. It is a stroke
 * a quarter of the width it claims.)
 *
 * Every other measurement in this app is against the LONG edge — `logoStrokeFrac` divides by
 * `Math.max(w, h)`, `LOGO_BOX_NOMINAL_VB` is the long edge of the painted `<image>`, and
 * `presentedOuterOutline` reads this very ring back as `thickness × LOGO_BOX_NOMINAL_VB` before
 * handing it to the text mirror. This one function divided by the short edge, so the number's
 * mirrored copy was drawn at the mark's aspect ratio TIMES the ring the mark actually wore. On a
 * square badge the two agree and nothing moved for months; bobcats' wordmark is 1344×308, so its
 * ring paints 0.23 units while the number beside it wears the 1.00 the tool reported.
 *
 * Measured over the corpus capture before changing it: 29 plates carry an added ring, 21 of them on
 * marks square enough that the painted pixel count does not move at all, and the eight that do move
 * are led by bobcats cfg1/cfg3 (0.23u against a reported 1.00u) and `120039000589` cfg3 (0.32u) —
 * three of Ryan's own open flags, and the two he wrote this note about.
 *
 * @returns {Promise<{url:string, aspect:number|null}>}
 */
export async function addOutlineDataUrl(dataUrl, colorHex, thicknessFrac) {
  const color = normalizeHex(colorHex);
  if (!dataUrl || !color || !(thicknessFrac > 0)) return { url: dataUrl, aspect: null };
  const img = await decode(dataUrl);
  const [w, h] = rasterDims(img, dataUrl);
  if (!w || !h) return { url: dataUrl, aspect: null };
  const N = Math.max(1, Math.round(Math.max(w, h) * thicknessFrac));
  const in0 = drawToImageData(img, w, h);
  const outId = buildOutlinedImageData(in0, N, hexToRgb(color));
  const pw = outId.width, ph = outId.height;
  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(pw, ph)
    : Object.assign(document.createElement('canvas'), { width: pw, height: ph });
  const cx = canvas.getContext('2d', { willReadFrequently: true });
  cx.putImageData(new ImageData(outId.data, pw, ph), 0, 0);
  const url = canvas.convertToBlob
    ? await blobToDataUrl(await canvas.convertToBlob({ type: 'image/png' }))
    : canvas.toDataURL('image/png');
  return { url, aspect: pw / ph };
}

function drawToImageData(img, w, h) {
  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const cx = canvas.getContext('2d', { willReadFrequently: true });
  cx.imageSmoothingEnabled = false;
  cx.drawImage(img, 0, 0, w, h);
  return cx.getImageData(0, 0, w, h);
}

// ── browser wrapper: dataUrl → peeled dataUrl ─────────────────────────────────

/**
 * Peel `count` outer bands off a logo dataUrl, returning a NEW dataUrl (PNG, alpha
 * preserved). count 0 (or no bands) → the original url, untouched. Browser-only
 * (decode + canvas). Rejects are swallowed by the caller → falls back to the original.
 * @param {string} dataUrl
 * @param {string[]} bandsOuterToInner
 * @param {number} count
 * @returns {Promise<string>}
 */
export async function peelLogoDataUrl(dataUrl, bandsOuterToInner, count) {
  if (!dataUrl || !count || !bandsOuterToInner || !bandsOuterToInner.length) return dataUrl;
  const img = await decode(dataUrl);
  const [w, h] = rasterDims(img, dataUrl);
  if (!w || !h) return dataUrl;
  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const cx = canvas.getContext('2d', { willReadFrequently: true });
  cx.imageSmoothingEnabled = false;
  cx.drawImage(img, 0, 0, w, h);
  const id = cx.getImageData(0, 0, w, h);
  peelBandsFromImageData(id, bandsOuterToInner, count);
  cx.putImageData(id, 0, 0);
  if (canvas.convertToBlob) {
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return await blobToDataUrl(blob);
  }
  return canvas.toDataURL('image/png');
}

/**
 * Remap the logo's outline bands per a set of DISABLED band indices (outer→in) AND/OR
 * flatten its CENTER to `centerColor` (a hex, or null). Disabled inner bands adopt their
 * nearest enabled outer band's colour, outermost disabled bands peel; the center recolor
 * flattens all non-band opaque pixels. No edits → the original url.
 * @param {string} dataUrl
 * @param {string[]} bandsOuterToInner
 * @param {number[]} disabledIndices
 * @param {string|null} [centerColor]
 * @param {Object<number,string>|null} [bandColorHexes] - per-band override hexes, index→hex
 * @param {string|null} [fillHex] the mark's fill, for the label's edge-blend tie-break
 * @returns {Promise<string>}
 */
export async function remapLogoDataUrl(dataUrl, bandsOuterToInner, disabledIndices, centerColor, bandColorHexes, fillRemapHexes, fillHex) {
  const disabled = new Set((disabledIndices || []).map((i) => i | 0));
  const centerRgb = normalizeHex(centerColor) ? hexToRgb(centerColor) : null;
  const bandColors = {};
  if (bandColorHexes && typeof bandColorHexes === 'object') {
    for (const k of Object.keys(bandColorHexes)) { const hx = normalizeHex(bandColorHexes[k]); if (hx) bandColors[k | 0] = hexToRgb(hx); }
  }
  const hasOverride = Object.keys(bandColors).length > 0;
  // Contextual fill swaps (hex → rgb), applied to non-band interior pixels (see remapBands…).
  const fillRemap = Array.isArray(fillRemapHexes)
    ? fillRemapHexes.filter((s) => s && normalizeHex(s.from) && normalizeHex(s.to)).map((s) => ({
      from: hexToRgb(s.from),
      to: hexToRgb(s.to),
      /* WD 455 — a piece-scoped swap keeps the hex and the census it needs to re-flood its own
         component against the raster actually being repainted. */
      ...(s.pieceOnly ? { pieceOnly: true, fromHex: normalizeHex(s.from), tones: s.tones || null } : {}),
      ...(Array.isArray(s.keep) && s.keep.length ? { keep: s.keep.map(hexToRgb) } : {}),
    }))
    : [];
  const hasSwaps = !centerRgb && fillRemap.length > 0;
  if (!dataUrl || (!disabled.size && !centerRgb && !hasOverride && !hasSwaps)) return dataUrl;
  const img = await decode(dataUrl);
  const [w, h] = rasterDims(img, dataUrl);
  if (!w || !h) return dataUrl;
  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const cx = canvas.getContext('2d', { willReadFrequently: true });
  cx.imageSmoothingEnabled = false;
  cx.drawImage(img, 0, 0, w, h);
  const id = cx.getImageData(0, 0, w, h);
  remapBandsFromImageData(id, bandsOuterToInner || [], disabled, centerRgb, hasOverride ? bandColors : null, hasSwaps ? fillRemap : null, fillHex);
  cx.putImageData(id, 0, 0);
  if (canvas.convertToBlob) {
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return await blobToDataUrl(blob);
  }
  return canvas.toDataURL('image/png');
}

/* ⛔ A VECTOR IS RECOLOURED AT NO LESS THAN THE SIZE IT WAS ANALYSED AT (WD 470, bctornados plate 7).
   An SVG's `naturalWidth` is whatever its `width` attribute says — bctornados declares 25×32 — so every
   recolour, peel and added ring was painted on a 25-pixel raster and stretched onto the plate: Ryan saw a
   blurred, blocky keyline (*"the clarity of the logo"*). Floored at `SVG_ANALYSIS_EDGE`, the 256px box
   `prepareForDisplay` already rasterises a vector into for its palette and structure, so the paint and the
   analysis agree on what the mark is. Rasters are untouched — their pixels are real. */
const SVG_ANALYSIS_EDGE = 256;
function rasterDims(img, dataUrl) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h || globalThis.__wdNoSvgFloor || !/^data:image\/svg\+xml/i.test(String(dataUrl || ''))) return [w, h];
  const k = SVG_ANALYSIS_EDGE / Math.max(w, h);
  return k > 1 ? [Math.round(w * k), Math.round(h * k)] : [w, h];
}

function decode(dataUrl) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
}
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

/**
 * THE RENDER PALETTE — one builder, every caller (WD 413).
 *
 * `effectiveTextStyle` answers "what does the number wear here?" from the palette plus a set of
 * HINTS about the mark and the plate. Those hints were assembled by hand in THREE places — the
 * renderer, the design panel, and the corpus runner's dump — and this file already carries a
 * warning about exactly that shape, one level down:
 *
 *   > "THE SAME OPTIONS THE RASTER USES, VIA `bandViewOptions` — never a hand-built copy. This WAS
 *   >  a hand-built copy, and the day R11 added `ringed` to that object the dump started reporting
 *   >  a PEEL for 120018000234 cfg2 while the picture showed the band kept. A dump that disagrees
 *   >  with the render is worse than no dump: the whole pass gets scored on it."
 *
 * ⛔ AND IT HAPPENED AGAIN, ON THE PALETTE, THE SAME WAY (2026-09-16). WD 413 added
 * `logoInkWeight` to the two real builders; the runner's copy did not carry it, so the gap moat
 * changed in the PICTURE and not in the dump, and the 17-mark probe reported kingtornado
 * byte-identical when its number had just lost 1.5 units of ring. Caught only because the probe
 * named the mark the rule was built for and that mark was missing from the diff.
 *
 * Two silent divergences were already in place when this was written, and both are closed by it:
 * the runner additionally required `confidence === 'high'` for `logoOutlined` (agrees on all 137
 * marks today — latent, not active), and it carried no `pool` at all, so its `effectiveTextStyle`
 * ran without the buyer's swatches on the 90 marks that have them.
 *
 * @param {object} profile the design profile
 * @param {string} plateHex the plate the ink is painted on — NOT `profile.bodyColor` on a product
 *   that cannot be dyed (see ui/state `effectivePlateHex`, render-engine's `bodyColorOverride`).
 */
/**
 * THE MARK'S MOST PROMINENT TONE, AS DRAWN ON THIS PLATE — the left-hand side of the fill mirror.
 *
 * Ryan stated the rule himself (umobilerams cfg2, 2026-09-17): *"the logo has 2 prominent colors,
 * red and white. I can't tell which one is more prominent by %. whichever one it is, the # should
 * have that as it's fill color."*
 *
 * ⛔ AND THEN HE CORRECTED IT TWICE, IN THE SAME WORDS BOTH TIMES. The most prominent tone is not
 * always the answer: when it is the tone that owns the mark's OUTER EDGE it belongs on the number's
 * OUTLINE, and the FILL takes the next tone down.
 *     db-logo:      *"white center parts and green outer parts.. so the text should follow suit..
 *                     while B definitely doesn't look bad, IT'S JUST FLIPPED."*
 *     120150001516: *"the red part is CENTERED and the white/gray tones are on the OUTSIDE, so this
 *                     is reflected best with version A."*
 * Measured against every answer he has given on this question, the flip is worth three points:
 *     plain "most prominent"   26 of 31        with the flip   29 of 31
 * and all three it gains are on the match-question set, where it goes 16 of 16.
 *
 * ⚠ SHARES ARE READ AS DRAWN, THROUGH `presented` — peel, repaint, swap and centre — for the same
 * reason every other read in this file is: `mcdonalds-logo|cfg1` is 76% red, the plate IS that red,
 * the app PEELS the red band, and Ryan calls the yellow number *"right"*. Stored, the mark is red
 * and the number is wrong; as drawn, the mark is yellow and the number is right.
 *
 * ⚠ THE LADDER IS `toneTouches` (measured px) then `traceTones` (the raster census) — NOT
 * `palette.all`, which exists only in the corpus dump. That mistake shipped once already; see
 * `toneInkShare`.
 */
/**
 * RUNG 0 — the mark's STRUCTURE colours, as drawn on this plate.
 *
 * ⭐ Ryan, 2026-09-18, stating the rule the six inference passes never found:
 *   *"the outline is clearly black (inner) and white (outer) as well. So, the toss up is between a
 *   red weight number and a gray one, with a black inner outline then a white outer outline"*
 *
 * An outline is not a fill candidate, because the outline is ALREADY being mirrored — onto the
 * NUMBER'S outline. Offering it again as the fill double-counts it and hands the number the colour
 * of the logo's edge instead of the colour of its body.
 *
 * ⛔ WHY THE OUTER-EDGE OWNER AND NOT `ls.bands`. The band list is still computed against the brand
 * palette, and after WD 424 that palette can name a colour the tone table no longer holds —
 * mountmercy's band reads `#033c26`, a green/black average present in zero pixels, while the tones
 * are `#000000` and `#067A4D`. Matching hexes between the two lists finds nothing. The tone table's
 * OWN BG-touch column answers the same question from the raster and cannot drift from it: the tone
 * that faces the transparent background IS the silhouette, whatever the palette called it. Bands are
 * still folded in when they happen to match a tone — they carry the inner shells the BG test can't
 * see — but they are no longer what the rule stands on.
 *
 * ⚠ HIS FALLBACK IS LOAD-BEARING AND IS NOT A DEGRADED MODE: *"when there is no clearly defined
 * outline colour, every colour becomes a fill candidate"* — his words. That covers the 26 marks
 * (104 of 528 plates) whose structure read comes back low. So an empty structure set means
 * EVERYTHING competes, never "refuse to answer".
 */
function structureTones(p, plateHex, ls) {
  const out = new Set();
  const map = presentedMap(p, plateHex);
  const drawn = (h) => normalizeHex(repaintedTo(map, h) || h);
  const t = ls.toneTouches;
  if (t && typeof t === 'object') {
    let totalBG = 0;
    for (const k of Object.keys(t)) totalBG += ((t[k] && t[k].touches && t[k].touches.BG) || 0);
    if (totalBG > 0) {
      for (const k of Object.keys(t)) {
        const bg = (t[k] && t[k].touches && t[k].touches.BG) || 0;
        if (bg / totalBG >= 0.5) { const h = drawn(k); if (h) out.add(h); }
      }
    }
  }
  for (const b of (ls.bands || [])) { const h = drawn(b); if (h) out.add(h); }
  return out;
}

/**
 * The mark's most prominent tone AS DRAWN on this plate — the fill mirror's left-hand side.
 *
 * Rung 0 of Ryan's ladder (`WEIGHT-NUMBER-COLOUR-RULES.md`) filters the candidate set first; what
 * survives is ordered by prevalence (rung 2), corrected by rung 3 (the colour beats the neutral)
 * and tie-broken by rung 4 (whichever stands out best against the plate).
 * ⛔ **RUNGS 1, 3b AND 5 ARE STILL NOT IMPLEMENTED** — the plate-was-dyed-from-this-colour identity,
 * the light/dark character tie-break and the legibility veto. Each still has to be measured against
 * his worked examples rather than guessed at; rung 5 in particular has a measured DISPROOF of its
 * naive form. See the ladder doc before adding one.
 *
 * `opts.rung0 === false` restores the pre-WD-424 behaviour, for measuring the change against the
 * corpus without a git stash.
 */
export function markDominantTone(profile, plateHex, opts = {}) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const pres = presentedToneShares(p, plateHex, opts);
  if (!pres.length) return null;

  if (opts.rung0 === false) {
    let outerOwner = null;
    const t = ls.toneTouches;
    if (t && typeof t === 'object') {
      let totalBG = 0;
      for (const k of Object.keys(t)) totalBG += ((t[k] && t[k].touches && t[k].touches.BG) || 0);
      if (totalBG > 0) {
        for (const k of Object.keys(t)) {
          const bg = (t[k] && t[k].touches && t[k].touches.BG) || 0;
          if (bg / totalBG >= 0.5) { outerOwner = normalizeHex(k); break; }
        }
      }
    }
    if (outerOwner) {
      const mapped = repaintedTo(presentedMap(p, plateHex), outerOwner) || outerOwner;
      if (pres.length > 1 && colorsConflict(pres[0].hex, mapped)) return pres[1].hex;
    }
    return pres[0].hex;
  }

  return ladderPick(fillCandidates(p, plateHex, ls, pres), plateHex, opts);
}

/* ══ RUNG 3 — THE COLOUR BEATS THE NEUTRAL ═══════════════════════════════════════════════════════
 * > *"YES, black stands out better against white, but let's pick the color, it's more fun, when
 * >  given the choice between a color and a black or white or NEAR-BLACK OR NEAR-WHITE"*
 * >                                                                    — Ryan, bobcats-phsc cfg2
 *
 * ⛔ THE WRITTEN LADDER SAYS "near-even shares -> the colour wins, clearly-apart -> prevalence
 * wins", AND HIS OWN CARDS DO NOT FIT THAT. coastalcoyotes is black 58% against red 21% — a
 * 37-point gap, nowhere near even — and he took the red. polkeagles is black 49% against yellow 5%
 * and he took the black. The GAP does not separate his answers. What does, on all seven
 * colour-vs-neutral cards, is THE COLOUR'S OWN SHARE OF THE MARK:
 *
 *     colour LOST   at  4%, 4%, 5%      (virginia + wisconsin green, polkeagles yellow)
 *     colour WON    at  17%, 21%, 23%       (wexmoor red, coastalcoyotes red, mountmercy red)
 *
 * ⚠ RE-MEASURED 2026-09-18 AFTER THE NO-PREFERENCE PASS. bobcats-phsc cfg2 was the 13% datum that
 * used to close this bracket; asked again with a tossup button on the page, Ryan marked it EITHER.
 * It is not evidence and it never was — it was a forced coin flip. Bracket widened (5%, 13%) ->
 * (5%, 17%) and the threshold re-centred 9% -> 11%. Neither value changes any card in the set; the
 * point is that the number is the midpoint of what he actually claims, not of what he was cornered
 * into claiming.
 *
 * — which is exactly what he SAID about the one he rejected: *"yellow was just the beak of the
 * bird."* Not "yellow lost the count"; "yellow is a detail." A token accent is not what the mark
 * reads as, whatever the arithmetic says.
 *
 * ⭐ EVERY THRESHOLD BELOW IS THE MIDPOINT OF A MEASURED BRACKET, NOT A CHOSEN NUMBER — the doc's
 * own instruction ("measure it, do not choose it"). Re-measure before nudging any of them; the
 * brackets are stated so a future session can tell a calibrated value from a guess.
 */
/**
 * ⭐ HOW FAR AHEAD THE NEUTRAL HAS TO BE TO WIN — Ryan's own rule, stated 2026-09-18 (WD 426):
 * *"It's not so much what the % of the colour picked is, it's the DIFFERENCE between the colour %
 * and the neutral... when the neutral is nearly 2x more (~30% vs ~15%), the neutral should win."*
 *
 * ⛔ AND IT REPLACES AN ABSOLUTE FLOOR THAT HAD COLLAPSED TO NOTHING. The previous model asked only
 * how big the colour was, and across the day it was re-measured twice and ended with a bracket of
 * (15.3%, 15.9%) — six tenths of a point, which is a coincidence, not a threshold. The ratio model
 * fits every card with room on both sides.
 *
 * ⚠ IT ONLY FITS BECAUSE HE CHANGED TWO ANSWERS, AND HE CHANGED THEM AFTER SEEING THE PIXELS.
 * coastalcoyotes and wexmoor were the two cards that inverted every bracket all day — he had taken
 * the red on both, describing wexmoor as *"about 50/50 red vs black"*. Shown the keyed artwork
 * enlarged (`REVIEW-2026-09-18/keyed/`), he agreed both marks are mostly black and released them:
 * *"you're right, it is more black.. therefore, if my rule from before results in a change, that's
 * fine."* ⭐ The lesson is the method, not the number: when his reading and the census disagree by
 * 3x, SHOW HIM THE KEYED RASTER rather than tuning a threshold to fit his first answer.
 *
 * With those two released, only mountmercy still takes the colour, at 1.12x, and the next card up
 * is angry-panda's cream at 2.03x. Bracket **(1.12, 2.03)**; he proposed 1.35 and it sits inside.
 */
const RUNG3_NEUTRAL_RATIO = 1.35;
/**
 * A floor under the ratio, so a speck cannot win on proportion alone — at 0.5% against a 0.6%
 * neutral the ratio is 1.2 and the number would take the speck. ⚠ NOT MEASURED FROM HIS CARDS and
 * it does not need to be: the lowest colour share on any card is 4.0% and the ratio already
 * rejects every one of those. This is a guard on a degenerate case, not a second threshold.
 */
const RUNG3_COLOUR_MIN_SHARE = 0.05;
/** At or below this L*, a tone reads as his "black or near-black". Bracket: >=8, <28.
 *  ⚠ L*, NOT RGB — `isNearBlackHex` would miss angry-panda's #200B40 (r=32 clears its 24 bar), and
 *  that is the very tone he pointed at and called "the black". Measured L* 8. */
const RUNG3_NEAR_BLACK_L = 18;
/** At or above this L*, a tone reads as his "white or near-white". Bracket: >77, <=93.
 *  angry-panda's #FCE7D1 is L* 93 and C* 14 — chromatic by the app's own 12 bar, and he called it
 *  "white" twice. Lightness is what decides this, not chroma. */
const RUNG3_NEAR_WHITE_L = 85;
/** CIELAB chroma at/below which a tone is neutral regardless of lightness (the app's own bar). */
const RUNG3_MIN_CHROMA = 12;

export function isNeutralTone(hex) {
  const h = normalizeHex(hex);
  if (!h) return true;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const lab = srgbToLab([r, g, b]);
  const chroma = Math.hypot(lab[1], lab[2]);
  return chroma < RUNG3_MIN_CHROMA || lab[0] <= RUNG3_NEAR_BLACK_L || lab[0] >= RUNG3_NEAR_WHITE_L;
}

/**
 * Given the rung-0 candidates heaviest-first, return the hex the mirror should take.
 * Falls through to plain prevalence (rung 2) whenever the leader is already a colour, or no colour
 * holds enough of the mark to displace it — which is what keeps polkeagles' black.
 */
function rung3(cand, opts = {}) {
  if (!cand.length) return null;
  if (!isNeutralTone(cand[0].hex)) return cand[0].hex;   // leader is already a colour: rung 2 stands
  const ratio = typeof opts.rung3Ratio === 'number' ? opts.rung3Ratio : RUNG3_NEUTRAL_RATIO;
  const floor = typeof opts.rung3Min === 'number' ? opts.rung3Min : RUNG3_COLOUR_MIN_SHARE;
  const colour = cand.find((g) => !isNeutralTone(g.hex)
    && g.share >= floor && cand[0].share < ratio * g.share);
  return colour ? colour.hex : cand[0].hex;
}

/* ══ RUNG 4 — THE TIE-BREAK, AND NOTHING MORE ════════════════════════════════════════════════════
 * > *"the white vs light gray % on the logo appear to be ABOUT EQUAL, so go with the one that
 * >  stands out the best against the plate background color. since the plate is black, pick the
 * >  white"*                                                                — Ryan, 120150001516 cfg3
 * > *"picked the color as opposed to the neutral one, ESPECIALLY SINCE the plate is dark and the
 * >  brighter colour pops a bit better"*                                    — Ryan, mountmercy cfg1
 *
 * ⛔ HE GAVE THIS AS RUNG 2 AND IT IS LAST, AND THE DIFFERENCE IS NOT COSMETIC. A general contrast
 * PREFERENCE is not merely unsupported, it is DISPROVED: four of his worked examples deliberately
 * take the lower-contrast option (bobcats cr 1.82 over 20.82, mountmercy cfg2 4.36 over 21.00,
 * webster cfg2 1.50 over 16.10, coastalcoyotes), and the naive version was measured in WD 424 —
 * 55 plates render the number in EXACTLY the plate's hex and he APPROVED 43 of them. So rung 4
 * never overrules a rung above it. It only ever separates candidates the ladder left EQUAL.
 *
 * ⚠ "EQUAL" MEANS THE SAME KIND AND THE SAME SIZE. Same kind, because rung 3 has already ruled on
 * colour-vs-neutral and a tie-break that reopened it would be the general preference by the back
 * door. Same size, within `RUNG4_TIE_SHARE` — mountmercy's green and red are 23% and 23%, which is
 * the one card in the set that measures a tie at all.
 *
 * ⚠ WHAT IT FIXES, AND WHAT IT DOES NOT. mountmercy cfg1 + cfg4 (dark plates): green and red tie,
 * the plate is a near-black green, red pops, he takes red. ⛔ mountmercy cfg2 is a WHITE plate,
 * where green out-contrasts red 5.39 to 4.36, so this rule picks GREEN and the card still misses —
 * and the miss is honest. He never mentions the green on any mountmercy card; the cards offered him
 * red vs BLACK, so nothing he has said separates red from green on a light plate. The alternative
 * reading — "the BRIGHTER tone", his other word — would take red on all three, but no card he has
 * given separates "brighter" from "higher contrast", and the rule he stated in full sentences is
 * the plate-relative one ("SINCE the plate is black, pick the white"). ⭐ A page that offers
 * mountmercy's green against its red on a WHITE plate settles it in one question.
 */
/**
 * How close two shares must be to count as the tie this rung exists for.
 *
 * ⭐ MEASURED, ON THE ONE MARK IN THE SET THAT HAS A TIE. mountmercy's green is 22.76% and its red
 * 22.66% — **0.10 of a point apart**, which is the tie he described ("green 23% and red 23% are
 * tied"). Its yellow is 20.09%, **2.57 points** off the red, and he never called that a tie. So the
 * bracket is (0.10pt, 2.57pt) and the midpoint is 1.3.
 *
 * ⛔ AND THE UPPER BOUND IS NOT DECORATIVE — IT IS A MEASURED REGRESSION. At 3 points the tie group
 * swallows the yellow, the yellow out-contrasts everything on mountmercy's near-black plates, and
 * the tool starts drawing a YELLOW number on cfg1 and cfg4 — the two plates where the app drew RED
 * and Ryan said keep it. A tie-break that is allowed to reach three points stops being a tie-break.
 *
 * ⭐ RE-MEASURED WD 476 ON HIS NEAR-TIE RULE (*"if the dominant color is only dominant by 5% (or pick
 * a number which makes sense) ... go with the more contrasting of the two"*). Replayed over all 552
 * plates: the mountmercy regression above is GONE (its plates moved since; 0 mountmercy movers at 3
 * or 5). At 3 points exactly carrolllynx cfg2 + cfg4 move (dark vs white, 2.35 apart -> white, his
 * expectation); at 5 points 7 APPROVED plates follow (blazers 3.5 apart, 120081 4.2, 120153 4.5-4.9 -
 * small colours where 4 points is a 60% lead). Bracket (2.35, 3.5) -> 3. ⛔ Dropping the same-kind
 * condition at 5 points moves 22, and a RATIO bar (1.1 / 1.2 / 1.3) moves 2 / 3 / 6 - no better.
 */
const RUNG4_TIE_SHARE = 0.03;

/**
 * Among the candidates the ladder left tied with its pick, the one that stands out best against the
 * plate. `opts.rung4 === false` disables it; `opts.rung4Tie` overrides the bar, for measurement.
 */
function rung4(cand, winner, plateHex, opts = {}) {
  if (!winner || opts.rung4 === false || !plateHex) return winner;
  const tie = typeof opts.rung4Tie === 'number' ? opts.rung4Tie : RUNG4_TIE_SHARE;
  const w = cand.find((g) => normalizeHex(g.hex) === normalizeHex(winner));
  if (!w) return winner;
  const peers = cand.filter((g) => isNeutralTone(g.hex) === isNeutralTone(w.hex)
    && Math.abs(g.share - w.share) <= tie);
  if (peers.length < 2) return winner;
  let best = w, bestCr = contrastRatio(plateHex, w.hex);
  for (const g of peers) {
    const cr = contrastRatio(plateHex, g.hex);
    if (cr > bestCr) { best = g; bestCr = cr; }
  }
  return best.hex;
}

/** The ladder's answer for one candidate set: rung 2, corrected by rung 3, tie-broken by rung 4. */
function ladderPick(cand, plateHex, opts = {}) {
  return rung4(cand, rung3(cand, opts), plateHex, opts);
}

/**
 * The candidates in LADDER order, not prevalence order — apply the ladder, remove the winner, ask
 * again. ✅ **ON BY DEFAULT SINCE WD 427 — the condition WD 425 set for the flip was met.**
 *
 * ⭐ THE ARGUMENT FOR IT IS GOOD. The tail is raw prevalence, so the list disagrees with its own
 * head: the head is rung 3's answer and everything behind it is rung 2's. On mountmercy — black
 * 25%, red 23%, green 23% — the fill mirror correctly refuses the black and this list then offers
 * it again, one refusal later and invisibly, ranked ABOVE the red Ryan asked for. "The next colour
 * down" ought to mean the next one down THE LADDER. Turning it on carries mountmercy cfg2, the one
 * card rung 4 cannot reach, and takes the card score from 19 of 30 to 20.
 *
 * ⛔ AND IT IS HELD BACK ANYWAY, BECAUSE THIS PASS HAS TO STAY ATTRIBUTABLE. Measured on the app's
 * own recorded structures (`probe-twin-corpus-diff.mjs pass=REVIEW-2026-09-17`), rung 4 plus this
 * reorders the fallback list on **244 of 528 plates across 76 marks** — 46% of the corpus — while
 * changing the offline fill on exactly one. That gap is the problem, not the size: the fallback
 * only shows up in the picture when the mirrored fill is REFUSED, and this file cannot model the
 * refusal, so 243 of those 244 are changes whose rendered effect is unknown. WD 420-424 are already
 * unrendered and in this pass; adding an unpredictable 46% would mean any plate Ryan flags could
 * come from four places at once. Rung 4 alone moves 3 marks and every one can be named.
 *
 * ⭐ WHAT IT NEEDED — and WD 427 built it: model the refusal offline (`effectiveTextStyle`'s fill
 * mirror, not just `markDominantTone`) so the diff reports RENDERED fills. `probe-fill-reach.mjs`
 * verifies that model against the app's own `effFill` on 528 of 528 plates, rings included, and
 * `probe-twin-corpus-diff.mjs` now reports through it.
 *
 * ✅ AND THE ANSWER IS SMALL AND FULLY NAMED. Re-measured on REVIEW-2026-09-18 against the RENDERED
 * fill, the 244-of-528 figure above collapses to **2 plates, and both can be pointed at**:
 *     athletics-ecc-edu cfg2   grey #929192 -> red #C0272E   ledger says BAD    <- a repair
 *     bauerrevamp cfg3         navy #0B588E -> navy #0D3A60  ledger says APPROVED  <- the risk
 * The other 57 plates it touches move only the mirror's first choice or the fallback ORDER, and
 * draw an identical picture — which is the whole gap WD 425 could not see and refused to ship into.
 * The card score goes 19 of 30 -> 20 (`probe-ladder-score.mjs`), carrying mountmercy cfg2.
 * ⚠ `bauerrevamp` cfg3 is the one to look at first in the next render.
 */
function ladderOrder(cand, plateHex, opts = {}) {
  const rest = cand.slice();
  const out = [];
  while (rest.length) {
    const w = ladderPick(rest, plateHex, opts);
    const i = rest.findIndex((g) => normalizeHex(g.hex) === normalizeHex(w));
    out.push(rest.splice(i < 0 ? 0 : i, 1)[0]);
  }
  return out;
}

/** Rung 0 applied: `pres` minus the structure colours — never empty (his fallback, see above). */
/* ⭐ RULE 3 — A COLOUR THAT IS ONLY A KEYLINE IS NOT A FILL (Ryan 2026-09-20, on Chatham):
 * *"the green colour which is extracted for the weightfill colour seems like a bit of a stretch…
 * hardly seeing any green in the actual logo."* Mapped pixel by pixel, every green pixel in that
 * mark is a stroke one or two pixels wide — and the weight number was painted in it.
 *
 * ⛔ THE FAULT IS THIN **AND** MINOR, NOT THIN. That distinction is his, not mine: shown 24 plates
 * sorted by erosion survival he called 1.3% wrong and 1.6% fine, then 2.2% wrong and 2.7% fine. The
 * measurement that FOUND the defect does not DESCRIBE it. Wellesley's white survives 1.6% — thinner
 * than Loyola's cream — and he passed it, because it is 22% of the logo: a fifth of the mark drawn
 * as fine linework is a body, not a keyline. Loyola's cream is 11%.
 *
 * Both bars sit in the middle of a measured gap in his own answers:
 *     survival   must catch Loyola at 2.2 and spare wexmoor at 6.3   -> 4%
 *     share      must catch ECC at 11.6 and spare HSU at 20.9        -> 16%
 *
 * ⚠ AND IT PREDICTS NOTHING NEW, WHICH IS THE HONEST LIMIT OF IT. Across all 488 plates it catches
 * exactly the five he judged wrong and not one he has not seen, so it encodes five answers rather
 * than generalising from them. It ships because its OUTPUT is reviewable — the replacement each of
 * those five gets is chosen by the ladder below, and those five plates are spotlighted in the next
 * full pass so he judges the result rather than the threshold. */
const KEYLINE_FILL_SURVIVAL_MAX = 0.04;
const KEYLINE_FILL_SHARE_MAX = 0.16;

/* RULE 4 - MAY THE WEIGHT NUMBER WEAR BLACK? Ryan, 2026-09-20, after answering 18 plates:
 *   "If there is black on the logo, either because that's its natural color, because we changed a
 *   colour on the logo from one colour to black b/c it was required, or because we introduced a
 *   black ring onto the logo, then it's fine to add to the weight number too. If there is no black
 *   on the logo due to none of those 3 things being true, then do NOT add any black to the weight
 *   number."
 *
 * Three channels, and they are his, in his order: the artwork's own census, a recolour or band
 * repaint that produced black, and the ring the tool itself added to the mark.
 *
 * MEASURED AGAINST THE ARTWORK ALONE this reads 55 plates and he calls 53 of them correct - the
 * mark is black on the plate in nearly all of them, just not in the file. Against the presented
 * mark it is 7, and two of those seven are Hanover: one wears the PLATE COLOUR as a moat around
 * the number (its logo has 33.6% see-through negative space, so the plate shows through the mark
 * too) and the other's census is empty, i.e. unmeasured rather than black-free.
 * So: a ring that IS the plate is not black paint, and a mark with no census at all abstains.
 */
const BLACK_MAX_SPREAD = 18;
const BLACK_MAX_LUMA = 0.25;

/** Black in the sense his sentence means: a grey, and a dark one. Not a dark navy, not a plate. */
export function isBlackInk(hex) {
  const h = normalizeHex(hex);
  if (!h) return false;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return Math.max(r, g, b) - Math.min(r, g, b) <= BLACK_MAX_SPREAD && relLuminance(h) < BLACK_MAX_LUMA;
}

/**
 * Does the mark wear black ON THIS PLATE, by any of his three channels?
 * Returns null when the mark carries no census at all - unmeasured is not the same as black-free,
 * and the caller leaves such a plate alone rather than acting on a blank.
 */
export function markWearsBlack(profile, plateHex) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const trace = (Array.isArray(ls.traceTones) && ls.traceTones)
    || (Array.isArray(p.logoTraceTones) && p.logoTraceTones) || [];
  if (!trace.length) return null;                                   // unmeasured: abstain
  if (trace.some((t) => t && (t.share || 0) >= 0.03 && isBlackInk(t.hex))) return true;
  for (const sw of (contextualFillSwap(p, plateHex) || [])) {
    if (isBlackInk(sw && sw.to)) return true;
  }
  const bands = Array.isArray(ls.bands) ? ls.bands.map(normalizeHex).filter(Boolean) : [];
  if (bands.length) {
    const view = bandConflictView(bands, normalizeHex(plateHex), bandViewOptions(p));
    for (const k of Object.keys(view.repaint || {})) if (isBlackInk(view.repaint[k])) return true;
  }
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo');
  const ao = Array.isArray(logoEl && logoEl.logoAddOutline) ? logoEl.logoAddOutline
    : ((logoEl && logoEl.logoAddOutline) ? [logoEl.logoAddOutline] : []);
  if (ao.some((a) => a && a.thickness > 0 && isBlackInk(a.color))) return true;
  return false;
}

/** A ring the same colour as the plate is a MOAT, not black paint - see `markWearsBlack`. */
export function isPlateMoat(hex, plateHex) {
  return !!(hex && plateHex && colorsIndistinguishable(normalizeHex(hex), normalizeHex(plateHex)));
}

/** The mark's tones that are all stroke and little of it — never the weight number's fill. */
export function keylineOnlyTones(p) {
  const ls = (p && p.logoStructure) || {};
  const subs = (Array.isArray(ls.toneSubstance) && ls.toneSubstance)
    || (Array.isArray(p && p.logoToneSubstance) && p.logoToneSubstance) || [];
  return subs
    .filter((t) => t && typeof t.survival === 'number' && typeof t.share === 'number'
      && t.survival < KEYLINE_FILL_SURVIVAL_MAX && t.share < KEYLINE_FILL_SHARE_MAX)
    .map((t) => normalizeHex(t.hex))
    .filter(Boolean);
}

/* ⛔ AND IT IS NOT APPLIED HERE, WHICH COST A BUILD. The obvious home for rule 3 is the weight
 * number's fill candidates - and on the five plates Ryan judged it changes nothing, because their
 * number is not painted from this ladder at all. Chatham's green arrives as `palette.secondary`:
 * the fill starts as the role `primary`, collides with a plate dyed from the mark's own purple,
 * and `fillContrastRemap` swaps it for another entry in the PALETTE pool. The keyline tone was
 * chosen upstream of everything here, by the ROLE ASSIGNMENT in palette extraction.
 *   Wired in at this call site it moved four plates of `bobcats-phsc-edu`, a mark he has never
 * judged, and none of his five. So the measurement stays (it is what the real fix needs) and the
 * rule does not ship until it is measured where the choice is actually made.
 *   ⚠ A second thing that measurement showed: with these bars, SIX of ECC's nine census tones and
 * SEVEN of Chatham's eight qualify as keyline-only, because most minor tones in any mark are
 * anti-alias ridges. Whatever consumes this must expect that, not a shortlist. */
function fillCandidates(p, plateHex, ls, pres) {
  const structure = structureTones(p, plateHex, ls);
  if (!structure.size) return pres;
  const kept = pres.filter((g) => ![...structure].some((h) => colorsConflict(g.hex, h)));
  // A SPECK IS NOT A BODY (WD 476, Ryan on ncurams: "white is CLEARLY the prominent logo color, yet
  // the numbers filled in with minority colors"). When rung 0 leaves only a tone under rung 3's own
  // speck floor (ncurams' gold 3.3%, behind a 59% white edge), the mark has no body left to mirror,
  // so the edge competes again - his fallback above. ⛔ "Keep a WHITE edge" was measured first: 37
  // approved plates move (bobcats, elite-edge, gclancers...); this one moves 4 (ncurams 1-3, gopatriot 3).
  if (kept.length && kept[0].share < RUNG3_COLOUR_MIN_SHARE) return pres;
  return kept.length ? kept : pres;
}

/**
 * ⭐ WHAT THIS PLATE REPAINTS `hex` TO — the CAUSE 2 FIX (WD 427).
 *
 * `presentedMap` is keyed by the hexes the BAND list and the SWAP ladder hold, and every consumer
 * looked its own hex up with `Map.get`, i.e. exact string equality. The band list is computed
 * against the brand palette and the tone table is measured off the raster, so the two name one ink
 * differently by construction (WD 424) — `athletics-gcc-edu` carries band `#ED1F25` and tone
 * `#EB1F25`, **ΔE 0.83**, and on its own red plate the repaint was found, applied to the artwork,
 * and then not carried to the tone: the number kept the old red while the mark changed underneath
 * it. Three call sites, one miss each. `sameToneRow` is the lookup now.
 */
function repaintedTo(map, hex) {
  const h = normalizeHex(hex);
  if (!h || !map || !map.size) return null;
  if (map.has(h)) return map.get(h);
  for (const [from, to] of map) if (sameToneRow(from, h)) return to;
  return null;
}

/** `from -> to` for every recolour this plate applies to the artwork (swap / repaint / centre). */
function presentedMap(profile, plateHex) {
  const p = profile || {};
  const m = new Map();
  for (const sw of (contextualFillSwap(p, plateHex) || [])) {
    const a = normalizeHex(sw.from);
    const b = normalizeHex(sw.to);
    if (a && b) m.set(a, b);
  }
  const ls = p.logoStructure || {};
  const bands = (ls.bands || []).map(normalizeHex).filter(Boolean);
  if (bands.length) {
    const view = bandConflictView(bands, plateHex, bandViewOptions(p));
    for (const k of Object.keys(view.repaint || {})) {
      const from = bands[Number(k)];
      const to = normalizeHex(view.repaint[k]);
      if (from && to) m.set(from, to);
    }
  }
  return m;
}

/* ══ THE SPLIT TWIN — BUILT, MEASURED, AND DELIBERATELY LEFT INERT (WD 425) ══════════════════════
 * One ink, two rows. Median-cut can spend two buckets on a single flat colour, and when it does the
 * ink's vote splits with it — bobcats' gold arrives as #E4BA52 7% + #E4BB20 6%, and 13% of a mark
 * shows up as two details neither of which clears rung 3's floor. The mechanism is real and the
 * repair is two lines. ⛔ **IT STILL SHOULD NOT SHIP, AND THE BAR BELOW IS 14 ON PURPOSE.** Three
 * measurements, in the order they were made:
 *
 * ⛔ 1 · THE SPATIAL TEST IS DISPROVED — and it was the one WD 424 recommended (*"the next attempt
 * should be SPATIAL — are the two tones the same REGION? — not colorimetric"*). The tone table's
 * own adjacency column answers it for free, and `probe-tone-fusion.mjs` runs it over all 132 marks:
 * bobcats' two golds share **5%** of their boundary. They are not one region — one gold sits inside
 * the white (67% of its edge), the other against the black (60%). The SAME ink, drawn in two
 * places, which is exactly what a region test cannot see. Nor does the measure ORDER them:
 * webster's navy seam, a real twin, scores 33%, while gomounties' white against its grey — dE 40.5,
 * plainly two inks — scores 29%.
 *
 * ⛔ 2 · THE CARD IT WAS BUILT FOR IS A COIN FLIP. bobcats-phsc cfg2 is the mark whose gold the
 * census halves, and on the re-ask with a NO PREFERENCE button Ryan marked it **either**. Swept
 * through `probe-ladder-score.mjs` at every bar from 16 to 45, the merge changes **no card he has
 * expressed a preference on** — it only costs one its second option and makes it unscorable.
 *
 * ⛔ 3 · AND THE BRACKET COLLAPSES ONCE YOU MEASURE THE RIGHT OUTPUT. At bar 20 the drawn FILL
 * moves on only 15 of 496 plates — which is what WD 425 first measured, and it was measuring the
 * wrong thing. `logoToneOrder` is the other output, and a merge DELETES a row from it: at bar 20
 * the fill or the fallback order moves on **139 of 496 plates across 36 marks**. `malonepioneers`
 * is the proof it is not cosmetic — its white and its light grey are dE **19.2** apart, they merge,
 * white vanishes from the fallback, and cfg2 draws the number in **exactly the plate's red** on a
 * plate the ledger had APPROVED. ridge-view closes it from the other side at 20.3. The must-merge
 * floor is webster's navy seam at 18.4, so the whole surviving window is **(18.4, 19.2)** — 0.8
 * wide, with a measured regression 0.8 away on one side and nothing asking for it on the other.
 *
 * ⭐ WHAT WOULD REOPEN THIS: a question page that shows him bobcats' gold split against bobcats'
 * gold merged, and mountmercy's green against its red on a WHITE plate. Not another threshold.
 * ⚠ The knob stays so the finding can be re-run in one command, not because it is half-built:
 * `probe-twin-corpus-diff.mjs base=twinDeltaE=14 twinDeltaE=20`.
 */
/** `colorsConflict`'s own bar, which makes `sameInk` exactly the pre-425 behaviour. ⛔ Raising it is
 *  the change measured and rejected above — re-read that before you do. */
const TWIN_DELTA_E = 14;
/** Two tone rows that are the same ink. `opts.twinDeltaE` overrides the bar for measurement. */
function sameInk(a, b, opts = {}) {
  if (colorsConflict(a, b)) return true;
  const bar = typeof opts.twinDeltaE === 'number' ? opts.twinDeltaE : TWIN_DELTA_E;
  if (!(bar > 14)) return false;
  const la = labOf(a), lb = labOf(b);
  return !!(la && lb && deltaE(la, lb) < bar);
}
function labOf(hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  return srgbToLab([1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));
}

/** The mark's tone shares AS DRAWN on this plate, heaviest first, twins merged at `TWIN_DELTA_E`
 *  so one ink split across two census rows does not split its own vote. */
function presentedToneShares(profile, plateHex, opts = {}) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo');
  const centre = normalizeHex(logoEl && logoEl.logoCenterColor);
  if (centre) return [{ hex: centre, share: 1 }];           // flattened: one tone everywhere

  const raw = new Map();
  const t = ls.toneTouches;
  if (t && typeof t === 'object') {
    for (const k of Object.keys(t)) {
      const h = normalizeHex(k);
      const px = (t[k] && t[k].px) || 0;
      if (h && px) raw.set(h, (raw.get(h) || 0) + px);
    }
  }
  if (!raw.size) {
    const trace = (Array.isArray(ls.traceTones) && ls.traceTones)
      || (Array.isArray(p.logoTraceTones) && p.logoTraceTones) || [];
    for (const x of trace) {
      const h = normalizeHex(x && x.hex);
      const sh = (x && x.share) || 0;
      if (h && sh) raw.set(h, (raw.get(h) || 0) + sh);
    }
  }
  if (!raw.size) return [];

  // a PEELED band is not drawn at all; a repainted or swapped tone carries its share to the new one
  const bands = (ls.bands || []).map(normalizeHex).filter(Boolean);
  if (bands.length) {
    const view = bandConflictView(bands, plateHex, bandViewOptions(p));
    for (const i of (view.peel || [])) {
      const h = bands[Number(i)];
      if (h) raw.delete(h);
    }
  }
  const map = presentedMap(p, plateHex);
  for (const [from, to] of map) {
    /* ⛔ THE ROW TO MOVE IS THE ONE THIS REPAINT IS ABOUT, NOT THE ONE SPELLED THE SAME WAY.
       `raw` is keyed off the raster census and `from` off the band/palette list; an exact
       `raw.has(from)` silently skipped gcc's repaint over a ΔE of 0.83. See `repaintedTo`. */
    const row = raw.has(from) ? from : [...raw.keys()].find((k) => sameToneRow(k, from));
    if (row === undefined) continue;
    raw.set(to, (raw.get(to) || 0) + raw.get(row));
    raw.delete(row);
  }

  const total = [...raw.values()].reduce((a, b) => a + b, 0) || 1;
  const groups = [];
  for (const [hex, v] of [...raw.entries()].sort((a, b) => b[1] - a[1])) {
    const hit = groups.find((g) => sameInk(g.hex, hex, opts));
    if (hit) hit.share += v / total;
    else groups.push({ hex, share: v / total });
  }
  return groups.sort((a, b) => b.share - a.share);
}

/**
 * THE LADDER'S CANDIDATE SET, WITH ITS SHARES — what rungs 2/3/4 are actually looking at.
 *
 * Read-only, and it exists because three separate sessions have had to guess at these numbers from
 * the outside and two of them guessed wrong. A question page that asks Ryan about a threshold has
 * to print the same shares the rule reads, or it is asking about something else.
 */
export function markToneCandidates(profile, plateHex, opts = {}) {
  const p = profile || {};
  const pres = presentedToneShares(p, plateHex, opts);
  if (!pres.length) return [];
  const cand = opts.rung0 === false ? pres : fillCandidates(p, plateHex, p.logoStructure || {}, pres);
  return cand.map((g) => ({ hex: g.hex, share: g.share, neutral: isNeutralTone(g.hex) }));
}

/** Every tone the mark shows on this plate, heaviest first — `markDominantTone`'s [0] and its tail,
 *  with the same flip applied so the outer-edge owner is not offered as a fill. */
export function markToneOrder(profile, plateHex, opts = {}) {
  const p = profile || {};
  const pres = presentedToneShares(p, plateHex, opts);
  if (!pres.length) return [];
  const top = markDominantTone(profile, plateHex, opts);
  /* ⚠ The TAIL takes rung 0 too. This list is what the resolver falls back to when the mirrored
     fill is refused (Abbey: *"never do two same colors on top of each other like this with text"*),
     and a fallback that re-offers the outline would reintroduce exactly what rung 0 just removed —
     one refusal later, and invisibly. The structure tones are appended AFTER the candidates rather
     than dropped, so a mark whose every tone is structural still has somewhere to go. */
  const cand = opts.rung0 === false ? pres : fillCandidates(p, plateHex, p.logoStructure || {}, pres);
  const laddered = opts.rung0 === false || opts.ladderOrder === false
    ? cand : ladderOrder(cand, plateHex, opts);
  const order = laddered.map((g) => g.hex)
    .concat(pres.map((g) => g.hex).filter((h) => !cand.some((c) => colorsConflict(c.hex, h))));
  const rest = order.filter((h) => !top || !colorsConflict(h, top));
  return (top ? [top] : []).concat(rest);
}

/* WD 480 - THE LOGO'S OWN EDGE, FOR A WEIGHT NUMBER LEFT BARE. A band-less logo whose wrap holds the frontier
 * (`WRAP_FRONTIER_OWN_MIN`) and shows on this plate: its visible edge colour. Not part of `presentedLogoStack` on
 * purpose - the ring veto reads that stack, and the wrap there strips approved rings (measured, WD 474). Only the
 * number's legibility guard in `effectiveTextStyle` reads it. Lake Erie 25 lb (Ryan: *"the 25 should get an outline
 * though. it's a bug that it doesn't"*): the logo lost its ring, the number had nothing to mirror, dark on dark. */
export function logoEdgeWrapFor(profile, plateHex) {
  const p = profile || {};
  const ls = p.logoStructure || {};
  const plate = normalizeHex(plateHex);
  const wrap = normalizeHex(ls.wrapTone);
  if (!plate || !wrap || (Array.isArray(ls.bands) && ls.bands.length)) return null;
  if (frontierShare(ls.toneTouches, wrap) < WRAP_FRONTIER_OWN_MIN || colorsConflict(wrap, plate)) return null;
  return { hex: wrap, weight: wrapEdgeWeight(p, ls, wrap, plateHex) };
}

export function renderPaletteFor(profile, plateHex) {
  const p = profile || {};
  const base = p.palette || { primary: '#000000', secondary: '#FFFFFF', tertiary: null };
  const ls = p.logoStructure;
  const lo = presentedLogoStack(p, plateHex);
  const outer = lo.length ? lo[0] : null;
  return {
    ...base,
    // the buyer's loaded swatches, so a plate-conflict auto-recolour picks from THEIR colours
    pool: contrastPoolFrom(base, p.swatches),
    // does the mark have natural outline bands? selects the matching-plate rule
    logoOutlined: !!(ls && Array.isArray(ls.bands) && ls.bands.length > 0),
    // does the mark's keyline stand OFF its fill? gives the type the same plate-coloured moat
    logoGap: logoGapFromStructure(ls),
    // plate-dependent: are the mark's bands visible here? false -> the type presents bare too
    logoBandsVisible: bandsVisibleOnPlate(p, plateHex),
    // plate-dependent: what does the MARK show outermost here? the mirror's left-hand side
    logoOuterTone: outer ? outer.hex : null,
    logoOuterWeight: outer ? outer.weight : 0,
    /* plate-dependent: the WHOLE stack the logo shows here, outer -> inner. `logoOuterTone` is
       its head and stays because a dozen call sites read it; this is what the text mirror needs
       once the question is the whole lockup rather than the outermost edge (WD 453). */
    logoStack: lo.map((b) => ({ hex: b.hex, weight: b.weight })),
    logoEdgeWrap: logoEdgeWrapFor(p, plateHex),
    // plate-INDEPENDENT: how heavy is this mark's own linework, in text-ring units? sizes the moat
    logoInkWeight: outlineThicknessFor(p),
    // plate-dependent: the mark's most prominent tone AS DRAWN here — the fill mirror's left side
    logoDominantTone: markDominantTone(p, plateHex),
    /* ...and the rest of the mark's tones behind it, heaviest first. Needed because the fill mirror
       can be REFUSED: Ryan, on Abbey — *"never do two same colors on top of each other like this
       with text"* — so when the mirrored fill is already one of the number's own outlines, the
       resolver has to know what the next colour down is. */
    logoToneOrder: markToneOrder(p, plateHex),
  };
}
